/* scene3d/cabinetMath.js — PURE cabinet mesh-spec math for the 3D raster scene.
 * Ported VERBATIM (math only, no THREE) from lhc-app.html:
 *   getCabinetStartX (~31124), computeTotalWidth (~31156), getBackWall (~31195),
 *   kitchenBackZ (~31474), placeKitchenModule (~32634 — carcass/fronts/handles/door
 *   pivots/drawer units/kickboard/counter slab), buildSofa iteration (~32804),
 *   svgDoorCount/svgDrawerCount/svgHingeSide (~34112), buildBacksplashMesh (~33418).
 *
 * Every world position reuses engine/placement.js (cabinetWorldPos — parity-tested);
 * NOTHING is re-derived. Outputs are plain part-spec objects; sceneBuilders.js
 * instantiates them as THREE meshes/groups. Units = METRES.
 *
 * Parity notes preserved on purpose:
 *  - Island placements (island:true, no wallId) fall through buildSofa's row-layout
 *    branch in the live engine (buildSofa gates on `p.wallId` only), so the raster
 *    scene places them in the back-wall row exactly like the engine does.
 *  - kickboard y=0.04 and counter y=height+0.019 are ABSOLUTE (the engine does not
 *    apply the elevation override to them) — kept identical.
 */
import { WALL_THICKNESS } from "../../engine/constants";
import { roleBaseY, getWorktopTopM, getUpperLineM, ruleVal } from "../../engine/rules";
import { cabinetWorldPos } from "../../engine/placement";
import { cutsOf, golaCutRectM, clipPartPlan, clipFacePanel } from "../../engine/cabinetCuts";
import { golaNotchSides, golaNotchCuts, golaRebateSides, GOLA_NOTCH_MM } from "../../engine/golaClearance";
import { defDoorCount, defDrawerCount, configHingeSide, configHingeExplicit } from "../../engine/elevation";
import { frontRowsOf } from "../../engine/frontLayout";
import { buildGolaRuns, buildGolaPosts, golaPostSeat, golaProfileById, golaGeomOf } from "../../engine/golaRuns";
import { buildSurfaces, surfaceMaterial, surfaceSpecOf, worktopTopFor, resolveOverride } from "../../engine/surfaces";
import { effDef } from "../../engine/pricing";
import { resolveTok } from "../../engine/catalogIds";
import { isKitchenDef } from "../../engine/families";

/** Door-leaf count from the catalog code ("D2" → 2; drawer codes "DR…" → 0). (~34112) */
export function svgDoorCount(code) {
  if (!code) return 1;
  const c = code.toUpperCase();
  if (c.indexOf("DR-") === 0 || /DR\d/.test(c)) return 0;
  const m = c.match(/D(\d)/);
  return m ? parseInt(m[1], 10) : 1;
}
/** Drawer count from the catalog code ("DR3" → 3). (~34119) */
export function svgDrawerCount(code) {
  if (!code) return 0;
  const c = code.toUpperCase();
  const m = c.match(/DR(\d)/);
  return m ? parseInt(m[1], 10) : 0;
}
/** Hinge side from the catalog code suffix ("-R"/"-L"), else "N". (~34125) */
export function svgHingeSide(code) {
  if (!code) return "L";
  const c = code.toUpperCase();
  if (c.slice(-2) === "-R") return "R";
  if (c.slice(-2) === "-L") return "L";
  return "N";
}

/** The back wall = the horizontal wall with the smallest z midpoint. (~31195) */
export function getBackWall(walls) {
  if (!walls || walls.length === 0) return null;
  let back = null;
  let backZ = Infinity;
  walls.forEach((w) => {
    const isHorizontal = Math.abs(w.start[1] - w.end[1]) < 0.05;
    if (!isHorizontal) return;
    const mz = (w.start[1] + w.end[1]) / 2;
    if (mz < backZ) { backZ = mz; back = w; }
  });
  return back;
}

/** World Z of the back face of back-row cabinets (inner wall face + 5mm gap). (~31474) */
export function kitchenBackZ(walls) {
  if (!walls || walls.length === 0) return -0.30; // fallback: slight back-shift from origin
  let backZ = Infinity;
  walls.forEach((w) => {
    const isHorizontal = Math.abs(w.start[1] - w.end[1]) < 0.05;
    if (isHorizontal) {
      const mz = (w.start[1] + w.end[1]) / 2;
      if (mz < backZ) backZ = mz;
    }
  });
  if (backZ === Infinity) return -0.30;
  return backZ + WALL_THICKNESS / 2 + 0.005; // 5mm gap between cabinet and wall
}

/** Total row width incl. per-placement clearances (no-wall fallback centering). (~31156) */
export function computeTotalWidth(placements, lib) {
  return (placements || []).reduce((s, p) => {
    const d = effDef(p, lib);
    const w = (d && d.width) || 0;
    return s + w + (p.clrLeft || 0) + (p.clrRight || 0);
  }, 0);
}

/** Row origin X: back-wall min-x (inner corner when an L-side wall meets it),
 *  else centre the free-standing row. (~31124) */
export function getCabinetStartX(walls, placements, lib) {
  try {
    const wall = getBackWall(walls);
    if (wall) {
      const backMinX = Math.min(wall.start[0], wall.end[0]);
      const backZ = (wall.start[1] + wall.end[1]) / 2;
      const TOL = WALL_THICKNESS + 0.05;
      // Default for straight wall: cabinet starts at wall face (point 0)
      let startX = backMinX;
      (walls || []).forEach((w) => {
        if (w === wall) return;
        const isVertical = Math.abs(w.start[0] - w.end[0]) < 0.05;
        if (!isVertical) return;
        const wx = (w.start[0] + w.end[0]) / 2;
        if (Math.abs(wx - backMinX) < TOL) {
          const wMinZ = Math.min(w.start[1], w.end[1]);
          const wMaxZ = Math.max(w.start[1], w.end[1]);
          if (wMinZ <= backZ + TOL && wMaxZ >= backZ - TOL) {
            // L-shape: inner corner = side wall center + half thickness
            startX = wx + WALL_THICKNESS / 2;
          }
        }
      });
      return startX;
    }
  } catch (e) { /* fall through */ }
  return -computeTotalWidth(placements, lib) / 2;
}

/** How far a door swings when opened (fraction of a half-turn). 2026-08-26
 *  user: "when we open any door from cabinets make sure they open until 80
 *  degrees, nothing more" — it was 0.55 = 99°, past square and into the
 *  neighbour. 80° / 180° = 0.444…  */
export const DOOR_OPEN_TURN = 80 / 180;

const _SLIDE = 0.32;   // drawer slide-out distance (m)
/* FRONT REVEAL (2026-09-29 user: "the carcass should show the same in elevation — the bottom
 * shows a bit bigger than the sides or the top; it needs to be the same, 1.5 mm"): the fronts
 * cover the carcass front with ONE uniform reveal to the carcass edges on all four sides and a
 * 2 × reveal gap between two leaves or two rows. Before: 10 mm + a 4 mm latch-side gap on the
 * sides, 15 mm top and bottom, 8 mm between leaves — a different strip on every edge. The
 * carcass box itself sits BODY_INSET_M inside the module envelope (_cW / _cH below), so a
 * front's outer edge is BODY_INSET_M + FRONT_REVEAL_M from the envelope. */
export const FRONT_REVEAL_M = 0.0015;
export const BODY_INSET_M = 0.004;

/** cabinetPartsSpec(p, def, centerX, walls): the placeKitchenModule (~32634) mesh math as
 *  a pure spec — { moduleId, name, angle, parts:[…] } with part kinds:
 *    box        — { w,h,d, x,y,z, rotY, mat:'body'|'leg'|'counter', name }
 *    doorPivot  — hinge-pivot group at (x,y,z): leaf mesh at local leafOffX + optional
 *                 handle; closedRotY/openRotY drive the double-click open animation
 *    drawerUnit — slide group at (x,y,z): front/handle/box children in local coords,
 *                 openOffset (world vector) drives the slide-open animation           */
export function cabinetPartsSpec(p, def, centerX, walls, blob, specOpts) {
  // Cabinet center: X is set by row layout, Z is computed from the back wall,
  // Y depends on whether it's a wall-mounted cabinet or floor-standing.
  let cx, cz, angle = 0;
  const back = getBackWall(walls);
  /* FREE SIDE PANELS stand at THEIR x/z (2026-08-29): the historic parity
   * quirk row-lays wall-less placements — kept for every real cabinet, but a
   * free-standing end panel placed from Room → Side Panels must stand exactly
   * where it was dropped/dragged, oriented like an island (facing normal). */
  if (def.sidePanelEntry && p.island && p.x != null && p.z != null) {
    const pos = cabinetWorldPos(p, def, walls);
    cx = pos.cx; cz = pos.cz; angle = pos.angle;
  } else if (p.wallId && p.wallId !== (back && back.id)) {
    const pos = cabinetWorldPos(p, def, walls);
    if (pos) { cx = pos.cx; cz = pos.cz; angle = pos.angle; }
    else { cx = centerX; cz = kitchenBackZ(walls) + def.depth / 2; }
  } else {
    cx = centerX;
    cz = kitchenBackZ(walls) + def.depth / 2;
  }
  // Apply user-set angle override (degrees → radians, additive) and outset offset (mm → m)
  const _posOvr = (p.config && p.config.position) || {};
  if (_posOvr.angle) angle += _posOvr.angle * Math.PI / 180; // additive override, unchanged
  if (_posOvr.outset) {
    // v0.39: outset pushes the cabinet OUT along its facing normal
    cx += Math.sin(angle) * (_posOvr.outset / 1000);
    cz += Math.cos(angle) * (_posOvr.outset / 1000);
  }

  let y;
  if (def.role === "cabinet-wall" || def.role === "cabinet-stack" || def.role === "cabinet-bridge") y = roleBaseY(def) + def.height / 2; // v0.34
  else y = def.height / 2;
  if (_posOvr.elevation) y += _posOvr.elevation / 1000;

  const bodyMat = (def.role === "appliance") ? "leg" : "body";
  const parts = [];
  const _cW = def.width - 0.008, _cH = def.height - 0.008, _cD = def.depth - 0.025;
  /* ── CAD CUT ENGINE (2026-07-22 v2): per-instance prism cuts in cabinet-local
   * space (y = 0 at the BODY BOTTOM). Deep parts clip their plan region into
   * vertical bands; thin front panels clip their face profile — only parts the
   * prism actually crosses are modified. ── */
  /* AUTOMATIC GOLA REBATE (2026-08-26 user mandate: "so we can implement it
   * here without, like, cutting manually the notch — we click on gola and it
   * puts the gola based on its size, and it does a notch in the module").
   * Picking a profile now machines the standard 18mm-wide pocket on the gola
   * side(s) of THIS module (and, unchanged, on a neighbour standing at one),
   * as deep as the chosen profile's real section. Derived — never persisted:
   * clearing the gola removes it on the next rebuild, and the manual
   * Cut → GOLA Cut features still ride alongside for special cases.
   * (This reverses 2026-08-24o "only ME will cut" at the same user's
   * request; golaClearance.golaNotchCuts is the one geometry rule.) */
  /* MACHINED sides ≠ notch sides: a neighbour is only pocketed for a
   * DOUBLE-sided profile (engine/golaClearance.golaRebateSides). The caller
   * hands both in; a caller that only knows the notch sides (Present's local
   * spec) falls back to them, which is the pre-2026-08-26 behaviour. */
  const _gnAll = (specOpts && specOpts.golaRebateSides)
    || (specOpts && specOpts.golaNotchSides) || { L: false, R: false };
  const _golaDepM = (_gnAll.L || _gnAll.R)
    ? golaGeomOf(blob, golaProfileById(blob, (p.config || {}).gola)).depM : 0;
  /* SUPERSEDED MANUAL GOLA CUTS (2026-08-26 user: "the notch is still there
   * and its not removed"). His modules still carry the parametric GOLA Cut
   * features he authored back when the pocket had to be cut by hand — one
   * module holds TWO of them. Now that picking a profile machines the pocket
   * itself, those are a second, differently-sized slot beside the real one.
   * A module with an automatic rebate therefore ignores its authored gola
   * cuts: nothing is deleted, and clearing the gola brings them straight
   * back. Every OTHER kind of cut is untouched. */
  const _autoRebate = !!(_gnAll.L || _gnAll.R);
  const _cuts = cutsOf(p.config, def)
    .filter((c) => !(_autoRebate && c.golaCut))
    .concat(golaNotchCuts(def, _gnAll, { depthM: _golaDepM }));
  /* GOLA cuts rebate the BODY ONLY — doors/drawer fronts stay whole
   * (2026-08-24n: never the fronts, never the plinth). */
  const _faceCuts = _cuts.filter((c) => !c.golaNotch);
  const _yBase = y - def.height / 2;          // world y of cut-local y = 0
  const _H = def.height;
  /* THE FRONT ZONE STARTS ABOVE THE PLINTH (2026-09-15 user: "sometimes the
   * door opens with the plinth too — it should not happen"). A base cabinet's
   * leaf / drawer stack used to span the whole module (15mm..H−15mm) with the
   * 80mm kickboard drawn in FRONT of its bottom — so the lower part of every
   * front lived in the plinth zone and swung / slid out reading as "the
   * plinth". Fronts now start at the engine kickboard's top (world 0.08, the
   * same absolute parity quirk that draws the kick): a pivot holds the leaf
   * and nothing else. Wall / tall units (no engine kickboard) and a module
   * hung above its kick are unchanged. Cut-local metres (0 = body bottom). */
  /* PLINTH ZONE = THE PLATFORM PLINTH (2026-09-29 user: "in elevation the legs that hold the
   * cabinets and the plinth are not showing"): every floor-standing KITCHEN cabinet — base AND
   * tall — stands on its levelling feet behind a PLINTH_H (100 mm) board and its fronts begin
   * above it. The kickboard part below and the shared plinth runs (applyPlinthRuns) use the
   * SAME height, so a lone unit and a merged run read identically. A tall used to carry no
   * plinth at all (its doors started 15 mm off the floor) while every uploaded tall ships one;
   * the base kick was the iframe's 80 mm while the runs already drew 100. Furniture lines
   * (systems / indoor / outdoor) carry no kitchen plinth (engine/families). */
  const _standsOnPlinth = (def.role === "cabinet-base" || def.role === "cabinet-tall") && isKitchenDef(def);
  const _plinthH = _standsOnPlinth ? ruleVal("PLINTH_H") : 0;
  const _frontY0 = _standsOnPlinth ? Math.max(0, Math.min(_H - 0.1, _plinthH - _yBase)) : 0;
  /* THE FRONT ZONE (cut-local metres): rows partition [_rz0, _rz1] — the carcass front above
   * the plinth (or the carcass bottom edge on a hung unit) up to the carcass top — and every
   * front keeps FRONT_REVEAL_M to the zone edge, so two rows / two leaves sit 2 × reveal apart. */
  const _rz0 = Math.max(_frontY0, BODY_INSET_M), _rz1 = _H - BODY_INSET_M;
  const _fz0 = _rz0 + FRONT_REVEAL_M, _fz1 = _rz1 - FRONT_REVEAL_M;
  const _pushDeep = (rect, yLo, yHi, mat, role, name) => {
    // GOLA cuts touch the BODY only (2026-08-24n user: "it should cut
    // everything else EXCEPT door/fronts and plinth"): the kickboard ignores
    // golaNotch-flagged cuts; regular prism cuts still clip it.
    const _useCuts = role === "kick" ? _cuts.filter((c) => !c.golaNotch) : _cuts;
    const clip = _useCuts.length ? clipPartPlan(rect, yLo, yHi, _useCuts) : null;
    if (!clip) {
      parts.push({
        kind: "box", role,
        w: rect.x2 - rect.x1, h: yHi - yLo, d: rect.z2 - rect.z1,
        x: cx + Math.cos(angle) * ((rect.x1 + rect.x2) / 2) + Math.sin(angle) * ((rect.z1 + rect.z2) / 2),
        y: _yBase + (yLo + yHi) / 2,
        z: cz - Math.sin(angle) * ((rect.x1 + rect.x2) / 2) + Math.cos(angle) * ((rect.z1 + rect.z2) / 2),
        rotY: angle, mat, name,
      });
      return;
    }
    clip.bands.forEach((b, bi) => {
      if (b.full) {
        parts.push({
          kind: "box", role,
          w: rect.x2 - rect.x1, h: b.h, d: rect.z2 - rect.z1,
          x: cx + Math.cos(angle) * ((rect.x1 + rect.x2) / 2) + Math.sin(angle) * ((rect.z1 + rect.z2) / 2),
          y: _yBase + b.y0 + b.h / 2,
          z: cz - Math.sin(angle) * ((rect.x1 + rect.x2) / 2) + Math.cos(angle) * ((rect.z1 + rect.z2) / 2),
          rotY: angle, mat, name: name + " band " + (bi + 1),
        });
      } else {
        parts.push({
          kind: "shapeBand", role, region: b.region, h: b.h,
          x: cx, y: _yBase + b.y0, z: cz, rotY: angle, mat,
          name: name + " cut band " + (bi + 1),
        });
      }
    });
  };
  /* GOLA NOTE (2026-08-20f): the recessed profile sits INSIDE the carcass
   * volume behind the door — the routed pocket is authored in the
   * technician's GLB carcasses (their factory notch); the procedural
   * fallback box stays whole (5 consumers anchor on the single box carcass
   * part — never carve it into bands). */
  _pushDeep(
    { x1: -_cW / 2, z1: -_cD / 2, x2: _cW / 2, z2: _cD / 2 },
    _H / 2 - _cH / 2, _H / 2 + _cH / 2,
    bodyMat, "carcass", def.name + " carcass"
  );

  /* ── SIDE PANELS (2026-08-29 user: "side panels … stitch on the side of
   * that specific module, we can increase, cut that side panel"): the
   * technician's uploaded panel GLB, stood against the module's gable —
   * exactly the gola pattern: config picks the entry (cfg.sidePanel =
   * "sp-u<uid>") + the side (cfg.sidePanelPos L/R/LR), the engine sizes it
   * to the module (full height, carcass depth + 20mm door cover), and the
   * user can override height/depth per module (sidePanelHmm/sidePanelDmm).
   * The part carries `model`, so BOTH renderers fit the real GLB onto the
   * box; the box itself is the instant/procedural fallback. ADDITIVE like
   * equipment — a swap-GLB never contains this panel. */
  (() => {
    const _spCfg = p.config || {};
    const _spId = String(_spCfg.sidePanel || "");
    if (!_spId || _spId === "None" || !blob) return;
    const _spTok = /^sp-(.+)$/.exec(_spId);
    const _spK = _spTok ? resolveTok((blob && blob.sidePanels) || [], _spTok[1]) : -1;
    const _sp = _spK >= 0 ? blob.sidePanels[_spK] : null;
    if (!_sp) return;
    const _spT = Math.max(0.006, (Number(_sp.thicknessMm) || 18) / 1000);
    /* PER-SIDE ENTRY (2026-08-31 user: "I chose another side panel for ONE
     * side and it changes ALL — I want another material per side"): each
     * side may carry its own technician entry in sideCfg[side].sp (the
     * "u<uid>" token); absent -> the module-wide pick. Thickness, GLB and
     * finish all follow THAT side's entry. */
    const _spEntryOf = (side2) => {
      const sc2 = (_spCfg.sidePanelSideCfg || {})[side2] || {};
      if (sc2.sp) {
        const k2 = resolveTok((blob && blob.sidePanels) || [], String(sc2.sp));
        if (k2 >= 0 && blob.sidePanels[k2]) return blob.sidePanels[k2];
      }
      return _sp;
    };
    const _spTOf = (e2) => Math.max(0.006, (Number(e2.thicknessMm) || 18) / 1000);
    /* CAD SEAT (2026-08-29 user: "it needs to be CAD level, not random — the
     * side panel should match the cabinet BOX height, it stops right where
     * the kickboard begins, leaving a continuous recessed space for your
     * feet"). Derived from the SAME engine facts the gola height rule reads:
     * the kickboard part spans world y 0..0.08 on cabinet-base modules, so
     * the panel's auto bottom IS that kick top; roles without a kickboard
     * (wall / tall) start at their own module bottom. Top = the box top.
     * The four overrides are the precision toolset — Height, Depth, and the
     * signed X (along depth, + = toward the room) / Y (bottom up/down) mm
     * offsets — every number relative to the derived seat, like the gola's
     * X/Y drawings. */
    /* HIS FIT PICTURE (2026-08-29b): the panel runs FLOOR → BOX TOP, covering
     * the plinth zone from the side (the toe-kick recess stays open at the
     * FRONT only), and its front edge sits FLUSH with the door face — the
     * engine's own front plane (_fd = depth/2 − 12mm, leaf 20mm centred on
     * it → door face = depth/2 − 2mm). All CAD-derived; the Y tool lifts the
     * bottom (e.g. +80 to sit on the kickboard) when a job wants it. */
    const _spBot = _yBase;                                         // floor (or the hung bottom)
    const _spTop = _yBase + _H;                                    // the box top (under the worktop)
    const _spAutoH = _spTop - _spBot;
    /* POINT 0 (2026-08-30g user: "the side panel should start from point 0
     * and calculate from there — at the bottom it doesn't go all the way to
     * the back"): the anchor is the BOX's own back plane (module depth), not
     * the carcass back — the carcass is inset 12.5mm, which left every board
     * visibly short of the box at the back. Auto depth = the full box:
     * back plane → door face. */
    const _spBack = -def.depth / 2;                                // point 0 — the box back plane
    const _spFront = def.depth / 2 - 0.002;                        // ≡ door FACE (≡ _fd + leaf/2)
    const _spAutoD = _spFront - _spBack;                           // the WHOLE box, back → door face
    const _spHovr = _spCfg.sidePanelHmm > 0;
    const _spDovr = _spCfg.sidePanelDmm > 0;
    const _spH = _spHovr ? _spCfg.sidePanelHmm / 1000 : _spAutoH;
    const _spD = _spDovr ? _spCfg.sidePanelDmm / 1000 : _spAutoD;
    const _spDX = (Number(_spCfg.sidePanelXmm) || 0) / 1000;       // + = forward into the room
    const _spDY = (Number(_spCfg.sidePanelYmm) || 0) / 1000;       // + = bottom edge up
    const _spDZ = (Number(_spCfg.sidePanelZmm) || 0) / 1000;       // + = right along the wall (2026-08-30i)
    /* COLOUR (2026-08-29d): "match" follows the module's carcass pick; a
     * pool colour ("spc-<tok>") resolves to its hex; no pick keeps the
     * entry's own finish (uploaded file / technician swatch). The part
     * carries spPick so BOTH renderers paint the procedural board AND tint
     * the fitted GLB identically. */
    const _spcId = String(_spCfg.sidePanelColor || "");
    /* PER-SIDE COLOUR (2026-09-01 user: "I change the colour of one side and
     * it changes the whole side panel"): sideCfg[side].col = "match" |
     * "spc-u<uid>" | "" (explicit Own finish); undefined -> the module-wide
     * legacy pick. Resolved per board below. */
    const _spPickOf = (colId) => {
      const id2 = String(colId || "");
      if (id2 === "match") return "match";
      if (!id2) return null;
      const tok2 = /^spc-(.+)$/.exec(id2);
      const k2 = tok2 ? resolveTok((blob && blob.sidePanelColors) || [], tok2[1]) : -1;
      const c2 = k2 >= 0 ? blob.sidePanelColors[k2] : null;
      return c2 && c2.sw ? c2.sw : null;
    };
    const _spPickDefault = _spPickOf(_spcId);
    /* SIDES (2026-08-30c user: "remove Both — it needs to be left, right,
     * top, bottom"): the placement is a SET of letters — any combination of
     * L/R (vertical boards beside the gables) and T/B (horizontal cap boards
     * over/under the box). Legacy "LR" is simply the L+R combination. */
    const _spPosStr = String(_spCfg.sidePanelPos || "L");
    // "K" = the BACK panel (2026-08-31 user: "add one more, Back, for the
    // back of the module") — span-wide, box-tall, thickness-deep, behind.
    const _spPos = ["L", "R", "T", "B", "K"].filter((c) => _spPosStr.indexOf(c) >= 0);
    if (!_spPos.length) _spPos.push("L");
    /* SMART CAPS (2026-08-30d user: "top is not connected and bottom is
     * inside — top and bottom are not smart"). The T/B boards seat
     * THEMSELVES: always flush ON the box top / UNDER the box bottom — the
     * Y nudge belongs to the VERTICAL boards' bottom edge and must never
     * float a cap off the box (that was exactly his screenshot: Y +47 for
     * the side seat lifted the top cap into the air and pushed the bottom
     * one inside). And they SPAN the side panels when those exist — full
     * module width plus a thickness per fitted side — so the four boards
     * read as one connected frame. */
    /* PER-SIDE ROTATION (2026-08-30e user: "add a rotation button below
     * left/right/top/bottom for every side panel"; 2026-08-30f "I click
     * rotate and it doesn't rotate"): rotation is PHYSICAL — at 90°/270° the
     * board's two in-plane extents SWAP (a 600-tall × 561-deep left panel
     * becomes 561-tall × 600-deep), re-seated at the same back/bottom, so
     * the turn is visible even on a plain flat board. The panel model's
     * CONTENT turns with it (gola-profile pattern) about the panel's face
     * normal: local X for the vertical boards, local Y for the caps. */
    const _spRotMap = _spCfg.sidePanelRot || {};
    /* POSITION TOOLS (2026-08-30i user: "the grow buttons are not
     * professional — remove them; click the panel and move it in mm"): the
     * board is anchored CAD-style — back pinned on point 0, extra depth
     * grows forward — and the three signed mm fields move it: X front/back,
     * Y bottom up/down (vertical boards), Z left/right along the wall.
     * (sidePanelGrowX/GrowZ from the short-lived button row are ignored.) */
    const _spHasL = _spPos.indexOf("L") >= 0, _spHasR = _spPos.indexOf("R") >= 0;
    const _spCapW = (def.width || _cW)
      + (_spHasL ? _spTOf(_spEntryOf("L")) : 0) + (_spHasR ? _spTOf(_spEntryOf("R")) : 0);
    const _spCapLx = ((_spHasR ? _spT : 0) - (_spHasL ? _spT : 0)) / 2;
    _spPos.forEach((side) => {
      const vertical = side === "L" || side === "R";
      const back = side === "K";
      const _spE = _spEntryOf(side);                 // THIS side's material entry
      const _spTS = _spTOf(_spE);
      const _scCol0 = ((_spCfg.sidePanelSideCfg || {})[side] || {}).col;
      const _spPick = _scCol0 !== undefined ? _spPickOf(_scCol0) : _spPickDefault;
      const _spRotDeg = ((((Number(_spRotMap[side]) || 0) / 90 | 0) * 90) % 360 + 360) % 360;
      /* PER-PANEL RECORD (2026-08-30j user: "it's the position of the SIDE
       * PANEL, not the module — you select the side panel you want to
       * move"): each board can carry its own size/position in
       * cfg.sidePanelSideCfg[side] = {h,d,x,y,z} (mm). A missing field
       * falls back to the module-wide legacy keys, so old projects and
       * edit-all-at-once configs are untouched. */
      const _sc = (_spCfg.sidePanelSideCfg || {})[side] || {};
      const _sW = Number(_sc.w) > 0 ? Number(_sc.w) / 1000 : 0;   // caps: across override
      const _sH = Number(_sc.h) > 0 ? Number(_sc.h) / 1000 : _spH;
      const _sD = Number(_sc.d) > 0 ? Number(_sc.d) / 1000 : _spD;
      const _sDX = _sc.x != null ? (Number(_sc.x) || 0) / 1000 : _spDX;
      const _sDY = _sc.y != null ? (Number(_sc.y) || 0) / 1000 : _spDY;
      const _sDZ = _sc.z != null ? (Number(_sc.z) || 0) / 1000 : _spDZ;
      // a SELECTED cap may be lifted/dropped by ITS OWN y — the module-wide
      // Y still never floats caps (the 30d smart-cap fix stands)
      const _capDY = _sc.y != null ? (Number(_sc.y) || 0) / 1000 : 0;
      /* WIDTH + DEPTH, TWO FIXED DIRECTIONS (2026-08-30k user: "add width
       * and depth — two directions; the rotate-then-depth trick was not
       * professional"): every dimension has ONE meaning always — WIDTH runs
       * along the wall (caps; verticals are thickness-wide by construction),
       * DEPTH runs front-back, HEIGHT runs up. Rotation ONLY turns the
       * panel model's content (grain/profile) — it never re-aims a size.
       * Auto still = the box on that axis (fit the box first, 30h). */
      /* HEIGHT extensions (2026-08-30p user: the radio on Height too):
       * base = h0 (point 0 = the bottom seat) | legacy h | auto; hU extends
       * the TOP up, hD extends the BOTTOM down - each its own key. */
      const _sH0 = Number(_sc.h0) > 0 ? Number(_sc.h0) / 1000 : _sH;
      const _sHU = (Number(_sc.hU) || 0) / 1000, _sHD = (Number(_sc.hD) || 0) / 1000; // (_exM is declared later in this block - TDZ)
      const h2 = (vertical || back) ? Math.max(0.01, _sH0 + _sHU + _sHD) : _spTS;
      /* BASE + DIRECTIONAL EXTENSIONS (2026-08-30m user: "I do 600+100, a
       * radio appears Left/Right, I choose Left and it STICKS; +200 again,
       * the radio appears again, I choose Right — the Left must not change").
       * Every board carries a point-0 base plus four remembered extensions:
       *   width (caps):  base = w0 anchored on the box's LEFT corner (point
       *                  0) — or the legacy w/wExt anchor — or the auto
       *                  centred span; then − wL on the left, + wR right.
       *   depth:        base = d0 from the BACK corner — or legacy d/dExt —
       *                  or back → door face; then − dB back, + dF forward.
       * Each radio choice lands in ITS OWN key, so later increments never
       * disturb earlier ones. All mm, per selected side. */
      const _exM = (v) => (Number(v) || 0) / 1000;
      const _sWExt = String(_sc.wExt || "R");
      const _sDExt = String(_sc.dExt || "F");
      const _bw = def.width || 0;
      // width span (caps), local coords
      let _wA, _wB;
      if (Number(_sc.w0) > 0) { _wA = -_bw / 2; _wB = -_bw / 2 + _exM(_sc.w0); }
      else if (_sW) {
        _wA = _sWExt === "L" ? _bw / 2 - _sW : -_bw / 2;
        _wB = _sWExt === "L" ? _bw / 2 : -_bw / 2 + _sW;
      } else { _wA = _spCapLx - _spCapW / 2; _wB = _spCapLx + _spCapW / 2; }
      _wA -= _exM(_sc.wL); _wB += _exM(_sc.wR);
      // depth span, local z
      let _zA, _zB;
      if (Number(_sc.d0) > 0) { _zA = _spBack; _zB = _spBack + _exM(_sc.d0); }
      else if (Number(_sc.d) > 0 || _sD !== _spAutoD) {
        _zA = _sDExt === "B" ? _spFront - _sD : _spBack;
        _zB = _sDExt === "B" ? _spFront : _spBack + _sD;
      } else { _zA = _spBack; _zB = _spFront; }
      _zA -= _exM(_sc.dB); _zB += _exM(_sc.dF);
      const w2x = vertical ? _spTS : Math.max(0.01, _wB - _wA);
      const d2x = back ? _spTS : Math.max(0.01, _zB - _zA);
      // the BACK board stands OUTSIDE the rear face (point 0 back corner)
      const _lzS = back ? (_spBack - _spTS / 2 + _sDX) : (_zA + _zB) / 2 + _sDX;
      const lx = (vertical ? (side === "L" ? -1 : 1) * (_cW / 2 + _spTS / 2) : (_wA + _wB) / 2) + _sDZ;
      const y2 = (vertical || back)
        ? _spBot + _sDY - _sHD + h2 / 2            // bottom seat, minus the DOWN extension
        : (side === "T" ? _yBase + _H + _spTS / 2 : _yBase - _spTS / 2) + _capDY;
      parts.push({
        kind: "box", role: "sidePanel", mat: "body",
        w: w2x, h: h2, d: d2x,
        x: cx + Math.cos(angle) * lx + Math.sin(angle) * _lzS,
        y: y2,
        z: cz - Math.sin(angle) * lx + Math.cos(angle) * _lzS,
        rotY: angle,
        /* an IMAGE stored in `model` (Attach-3D accepts any file) is a
         * TEXTURE, never a GLB - loading a JPG as a model failed silently
         * and the board stayed plain (2026-08-31 "MERMER isn't working"). */
        ...(_spE.model && !/\.(jpe?g|png|webp|gif|avif)(\?|$)/i.test(String(_spE.model)) ? { model: _spE.model } : {}),
        // PHOTO -> MATERIAL (2026-08-31 user: "I uploaded a photo of a
        // MERMER — it should become a material just like the photo"): the
        // technician's photo upload (entry.tex, with sw = its dominant
        // colour) rides the part so BOTH renderers texture the board.
        /* the photo texture rides ALWAYS when present - it paints the
         * procedural board AND re-skins a fitted GLB (both renderers). */
        ...((_spE.tex || (/\.(jpe?g|png|webp|gif|avif)(\?|$)/i.test(String(_spE.model || "")) ? _spE.model : null))
          ? {
            tex: _spE.tex || _spE.model,
            // EXACT-IMAGE contract (2026-09-01): the photo's real coverage in
            // mm + grain rotation ride the part so both renderers scale it
            // true (engine/panelTexture -> swatchTexMat)
            ...(Number(_spE.texWmm) > 0 ? { texWmm: Number(_spE.texWmm) } : {}),
            ...(Number(_spE.texHmm) > 0 ? { texHmm: Number(_spE.texHmm) } : {}),
            ...(Number(_spE.texRot) ? { texRot: Number(_spE.texRot) } : {}),
            ...(Array.isArray(_spE.texPx) ? { texPx: _spE.texPx.slice(0, 2) } : {}),
          } : {}),
        ...(_spE.sw ? { sw: _spE.sw } : {}),
        ...(_spPick ? { spPick: _spPick } : {}),
        ...(_spRotDeg ? { spRot: _spRotDeg, spRotAxis: vertical ? "x" : "y" } : {}),
        name: def.name + " side panel " + side,
      });
    });
  })();

  // ── Real fronts: door leaves / drawer fronts + handles ──────────────────
  const _code = (def.code || p.type || "").toUpperCase();
  // EXPLICIT technician geometry wins; SKU D-patterns stay the fallback.
  const _doorCount = defDoorCount(def, _code);
  const _drawerCnt = defDrawerCount(def, _code);
  // a NEIGHBOUR's gola standing at this module's side (the notch sides) also
  // drives the hinge + kills the handle: the profile beside the cabinet IS
  // its grip now (2026-08-23 user: "automatically remove the handle bcs now
  // it has gola and the door should open the side of the gola").
  const _gnSides = (specOpts && specOpts.golaNotchSides) || null;   // ≡ _gnAll above
  // ONE hinge rule (engine/elevation.configHingeSide): explicit Opening pick →
  // neighbour-gola notch → AUTOMATIC follows the own-gola side → default.
  const _hinge = configHingeSide(def, _code, p.config, _gnSides);
  /* GOLA ⊻ HANDLE (2026-08-20 user mandate: "wheen u put gola u cant put
   * handle"): a gola profile IS the grip — no handle hardware renders on this
   * module's fronts. cfg.handle === "None" (the new Handle-tab option) also
   * removes the default bar. A neighbour's gola at either side counts too. */
  const _noHandle = !!((p.config && ((p.config.gola && p.config.gola !== "None")
    || p.config.handle === "None"))
    || (_gnSides && (_gnSides.L || _gnSides.R)));
  // an explicit side (Opening pick / single-side gola / neighbour notch) also
  // beats a layout row's authored hinge — the row value is a default, not a
  // lock (the user's "Right" used to be silently ignored on layout units).
  const _hingeExp = configHingeExplicit(p.config, _gnSides);
  const _fd = def.depth / 2 - 0.012;
  const _sinA = Math.sin(angle), _cosA = Math.cos(angle);
  /* DOORS NEVER CHANGE SIZE FOR A GOLA (2026-08-24 user: "the door changed
   * width … are not the same with gola like they are normally without gola —
   * check to not change structure"). The old front-strip narrowing (_gpSec,
   * the 2026-08-22 SketchUp round) shrank the door by the profile width; now
   * that the profile carves its REAL volume out of the carcass (and the
   * profile sits behind the door plane / in the gap), the fronts keep their
   * exact normal dimensions — with or without a gola. */
  const _gpL = 0;
  const _gpR = 0;
  const _gpC = (_gpL - _gpR) / 2;      // lateral centre shift for centred fronts
  const _gpW = _gpL + _gpR;            // total width the fronts give up
  // the fronts' lateral span: the carcass front less one reveal each side (uniform with the top / bottom)
  const _fx0 = -def.width / 2 + BODY_INSET_M + FRONT_REVEAL_M + _gpL;
  const _fwAll = def.width - 2 * (BODY_INSET_M + FRONT_REVEAL_M) - _gpW;
  /* n leaves across the span, 2 × reveal between neighbours: the leaf width and the centre of leaf i */
  const _leafWOf = (n) => (_fwAll - (n - 1) * 2 * FRONT_REVEAL_M) / n;
  const _leafCxOf = (n, i) => _fx0 + _leafWOf(n) / 2 + i * (_leafWOf(n) + 2 * FRONT_REVEAL_M);
  // local lateral offset (lx) + face depth (fd) → world position on the cabinet face
  const _fxz = (lx, fd) => ({ x: cx + _cosA * lx + _sinA * fd, z: cz - _sinA * lx + _cosA * fd });

  // FRONT LAYOUT VARIANTS (Phase 2): a selected technician layout replaces the
  // door/drawer fronts with its normalized row stack — the SAME drawerUnit /
  // doorPivot parts (dblclick + tour animate them) and the SAME per-panel cut
  // clipping, per row band instead of the whole face.
  const _rows = frontRowsOf(def, p.config);
  /* APPLIANCE FRONT OWNERSHIP (2026-08-18 user: "I put an oven inside this
   * cabinet but it's not really working — it doesn't open"): a configured
   * oven / dishwasher / integrated fridge IS the front — the module's own
   * doors/drawers + handles are suppressed so the appliance face never sits
   * on top of a working door. Tall units keep their fronts for an OVEN
   * (real oven towers have doors above and below the built-in). */
  const _eqOwnsFront = (() => {
    const c0 = p.config || {};
    const on = (k) => c0[k] && c0[k] !== "None";
    if (def.role === "appliance") return false;
    if (on("eqDishwashers") || on("eqFridges")) return true;
    if (on("eqOvens") && !(def.role === "cabinet-tall" || def.height > 1.4)) return true;
    return false;
  })();
  if (!_eqOwnsFront && _rows) {
    _rows.forEach((row, ri) => {
      const rowY0 = Math.max(row.y0, _rz0);                 // a row never reaches into the plinth zone
      const rowY1 = Math.min(row.y1, _rz1);                 // …nor past the carcass top
      const rowH = rowY1 - rowY0;
      if (row.kind === "open" || rowH <= 0.02) return;      // open niche: no front
      const rowC = (rowY0 + rowY1) / 2;                     // cut-local row centre
      const ly = rowC - def.height / 2;                     // offset from cabinet centre
      if (row.kind === "drawer") {
        const fw = _fwAll, fh = rowH - FRONT_REVEAL_M * 2;
        const c = _fxz(_gpC, _fd);
        const children = [];
        const _dfc = _faceCuts.length
          ? clipFacePanel(-fw / 2, fw / 2, rowC - fh / 2, rowC + fh / 2, _fd - 0.010, _fd + 0.010, _faceCuts)
          : null;
        if (!_dfc || !_dfc.gone) {
          children.push({
            w: fw, h: fh, d: 0.020, x: 0, y: 0, z: 0, mat: bodyMat, name: def.name + " drawer front",
            ...(_dfc ? { holes: _dfc.holes.map((hh) => ({ x1: hh.x1, y1: hh.y1 - rowC, x2: hh.x2, y2: hh.y2 - rowC })) } : {}),
          });
        }
        if (!_noHandle) children.push({ w: Math.min(0.14, fw * 0.45), h: 0.012, d: 0.014, x: 0, y: fh * 0.30, z: 0.017, mat: "leg", name: def.name + " handle" });
        const bd = Math.min(def.depth - 0.06, _SLIDE + 0.10), bh = Math.max(0.06, fh - 0.05);
        const bz = -0.012 - bd / 2;
        children.push({ w: 0.014, h: bh, d: bd, x: -fw / 2 + 0.022, y: -fh / 2 + bh / 2 + 0.012, z: bz, mat: bodyMat, name: def.name + " drawer box" });
        children.push({ w: 0.014, h: bh, d: bd, x: fw / 2 - 0.022, y: -fh / 2 + bh / 2 + 0.012, z: bz, mat: bodyMat, name: def.name + " drawer box" });
        children.push({ w: fw - 0.060, h: bh, d: 0.014, x: 0, y: -fh / 2 + bh / 2 + 0.012, z: -0.012 - bd + 0.007, mat: bodyMat, name: def.name + " drawer box" });
        children.push({ w: fw - 0.060, h: 0.012, d: bd, x: 0, y: -fh / 2 + 0.018, z: bz, mat: bodyMat, name: def.name + " drawer box" });
        parts.push({
          kind: "drawerUnit",
          x: c.x, y: y + ly, z: c.z, rotY: angle,
          openOffset: [_sinA * _SLIDE, 0, _cosA * _SLIDE],
          children,
        });
      } else {
        const nL = row.doors || 1;
        const _leafW = _leafWOf(nL);
        for (let di = 0; di < nL; di++) {
          const lx = _leafCxOf(nL, di);
          const hingeLeft = (nL > 1) ? (di === 0) : (((_hingeExp ? _hinge : (row.hinge || _hinge))) !== "R");
          const fw = _leafW, fh = rowH - FRONT_REVEAL_M * 2;
          const leafW = fw;                                  // the hinge edge IS the leaf edge (uniform reveal)
          const halfLeaf = leafW / 2;
          const hingeLx = lx + (hingeLeft ? -fw / 2 : +fw / 2);
          const piv = _fxz(hingeLx, _fd);
          const _dlLo = hingeLeft ? hingeLx : hingeLx - leafW;
          const _dlHi = hingeLeft ? hingeLx + leafW : hingeLx;
          const _dlc = _faceCuts.length
            ? clipFacePanel(_dlLo, _dlHi, rowC - fh / 2, rowC + fh / 2, _fd - 0.010, _fd + 0.010, _faceCuts)
            : null;
          const _dlCx = hingeLx + (hingeLeft ? halfLeaf : -halfLeaf);
          parts.push({
            kind: "doorPivot",
            x: piv.x, y: y + ly, z: piv.z,
            closedRotY: angle,
            openRotY: angle + (hingeLeft ? -1 : 1) * (Math.PI * DOOR_OPEN_TURN),
            leafW, leafH: fh, leafD: 0.020,
            leafOffX: hingeLeft ? halfLeaf : -halfLeaf,
            ...(_dlc && !_dlc.gone ? {
              leafHoles: _dlc.holes.map((hh) => ({
                x1: hh.x1 - _dlCx, y1: hh.y1 - rowC,
                x2: hh.x2 - _dlCx, y2: hh.y2 - rowC,
              })),
            } : {}),
            ...(_dlc && _dlc.gone ? { leafGone: true } : {}),
            handle: (def.role !== "appliance" && !_noHandle) ? {
              w: 0.012, h: Math.min(0.14, fh * 0.45), d: 0.014,
              x: hingeLeft ? (fw - 0.028) : -(fw - 0.028), z: 0.014,
            } : null,
            name: def.name + " door",
          });
        }
      }
    });
  } else if (!_eqOwnsFront && _drawerCnt > 0) {
    const _dH = (_rz1 - _rz0) / _drawerCnt;                // the stack fills the front zone
    for (let d = 0; d < _drawerCnt; d++) {
      const ly = (_rz1 - _dH * (d + 0.5)) - def.height / 2;
      const fw = _fwAll, fh = _dH - FRONT_REVEAL_M * 2;
      const c = _fxz(_gpC, _fd);
      // Group = front + handle + drawer box; the whole unit slides as one.
      const children = [];
      // Front panel (local z=0 is the cabinet face plane). If a cut prism
      // crosses THIS panel's face, its profile carries the holes (leaf-local).
      const _dfY = def.height / 2 + ly;         // panel centre, cut-local y
      const _dfc = _faceCuts.length
        ? clipFacePanel(-fw / 2, fw / 2, _dfY - fh / 2, _dfY + fh / 2, _fd - 0.010, _fd + 0.010, _faceCuts)
        : null;
      if (!_dfc || !_dfc.gone) {
        children.push({
          w: fw, h: fh, d: 0.020, x: 0, y: 0, z: 0, mat: bodyMat, name: def.name + " drawer front",
          ...(_dfc ? { holes: _dfc.holes.map((hh) => ({ x1: hh.x1, y1: hh.y1 - _dfY, x2: hh.x2, y2: hh.y2 - _dfY })) } : {}),
        });
      }
      // Handle — horizontal bar near the top of the front
      if (!_noHandle) children.push({ w: Math.min(0.14, fw * 0.45), h: 0.012, d: 0.014, x: 0, y: fh * 0.30, z: 0.017, mat: "leg", name: def.name + " handle" });
      // Drawer box behind the front (sides, back, bottom) — emerges when open
      const bd = Math.min(def.depth - 0.06, _SLIDE + 0.10), bh = Math.max(0.06, fh - 0.05);
      const bz = -0.012 - bd / 2;
      children.push({ w: 0.014, h: bh, d: bd, x: -fw / 2 + 0.022, y: -fh / 2 + bh / 2 + 0.012, z: bz, mat: bodyMat, name: def.name + " drawer box" });
      children.push({ w: 0.014, h: bh, d: bd, x: fw / 2 - 0.022, y: -fh / 2 + bh / 2 + 0.012, z: bz, mat: bodyMat, name: def.name + " drawer box" });
      children.push({ w: fw - 0.060, h: bh, d: 0.014, x: 0, y: -fh / 2 + bh / 2 + 0.012, z: -0.012 - bd + 0.007, mat: bodyMat, name: def.name + " drawer box" });
      children.push({ w: fw - 0.060, h: 0.012, d: bd, x: 0, y: -fh / 2 + 0.018, z: bz, mat: bodyMat, name: def.name + " drawer box" });
      parts.push({
        kind: "drawerUnit",
        x: c.x, y: y + ly, z: c.z, rotY: angle,
        openOffset: [_sinA * _SLIDE, 0, _cosA * _SLIDE],
        children,
      });
    }
  } else if (!_eqOwnsFront && _doorCount > 0) {
    const _leafW = _leafWOf(_doorCount);
    for (let di = 0; di < _doorCount; di++) {
      const lx = _leafCxOf(_doorCount, di);
      const hingeLeft = (_doorCount > 1) ? (di === 0) : (_hinge !== "R");
      // Door leaf on a hinge pivot: rotates open about its hinge edge. (~32712)
      // The leaf spans the FRONT ZONE (plinth top → carcass top, one uniform reveal):
      // _lcY is its centre in cut-local metres — the pivot sits at that height.
      const fw = _leafW, fh = _fz1 - _fz0;
      const _lcY = (_fz0 + _fz1) / 2;
      const leafW = fw;                                      // the hinge edge IS the leaf edge
      const halfLeaf = leafW / 2;
      const hingeLx = lx + (hingeLeft ? -fw / 2 : +fw / 2);
      const piv = _fxz(hingeLx, _fd);
      // Cut clipping for THIS leaf (closed position): holes land in LEAF-local
      // coords; leaves the prism misses stay untouched.
      const _dlLo = hingeLeft ? hingeLx : hingeLx - leafW;
      const _dlHi = hingeLeft ? hingeLx + leafW : hingeLx;
      const _dlc = _faceCuts.length
        ? clipFacePanel(_dlLo, _dlHi, _lcY - fh / 2, _lcY + fh / 2, _fd - 0.010, _fd + 0.010, _faceCuts)
        : null;
      const _dlCx = hingeLx + (hingeLeft ? halfLeaf : -halfLeaf);
      parts.push({
        kind: "doorPivot",
        x: piv.x, y: _yBase + _lcY, z: piv.z,
        closedRotY: angle,
        openRotY: angle + (hingeLeft ? -1 : 1) * (Math.PI * DOOR_OPEN_TURN),
        leafW, leafH: fh, leafD: 0.020,
        leafOffX: hingeLeft ? halfLeaf : -halfLeaf,
        ...(_dlc && !_dlc.gone ? {
          leafHoles: _dlc.holes.map((hh) => ({
            x1: hh.x1 - _dlCx, y1: hh.y1 - _lcY,
            x2: hh.x2 - _dlCx, y2: hh.y2 - _lcY,
          })),
        } : {}),
        ...(_dlc && _dlc.gone ? { leafGone: true } : {}),
        // Appliance doors carry no handle (engine: role check inside _addDoorLeaf).
        handle: (def.role !== "appliance" && !_noHandle) ? {
          w: 0.012, h: Math.min(0.14, fh * 0.45), d: 0.014,
          x: hingeLeft ? (fw - 0.028) : -(fw - 0.028), z: 0.014,
        } : null,
        name: def.name + " door",
      });
    }
  } else if (!_eqOwnsFront) {
    // Plain front fallback (no doors, no drawers) — engine _addFront(0,0,…,'door').
    const c = _fxz(_gpC, _fd);
    const _pfW = _fwAll, _pfH = _fz1 - _fz0;
    const _pfY = (_fz0 + _fz1) / 2;                          // front-zone centre (cut-local)
    const _pfc = _faceCuts.length
      ? clipFacePanel(-_pfW / 2, _pfW / 2, _pfY - _pfH / 2, _pfY + _pfH / 2, _fd - 0.010, _fd + 0.010, _faceCuts)
      : null;
    if (!_pfc || !_pfc.gone) {
      parts.push({
        kind: "box", role: "front",
        w: _pfW, h: _pfH, d: 0.020,
        x: c.x, y: _yBase + _pfY, z: c.z, rotY: angle, mat: bodyMat, name: def.name + " door",
        ...(_pfc ? { holes: _pfc.holes.map((hh) => ({ x1: hh.x1, y1: hh.y1 - _pfY, x2: hh.x2, y2: hh.y2 - _pfY })) } : {}),
      });
    }
  }

  /* PER-MODULE HORIZONTAL GOLA RAIL — REMOVED (2026-08-26 user: "man why its
   * showing horizontal gola? why arent u making this!").
   *
   * A gola pick used to also emit a 38mm strip across the top front of the
   * module. Two things were wrong with it:
   *   · the user's gola is a VERTICAL channel at the module's edge — a rail
   *     under the worktop is a different product he never asked for, and it
   *     rendered as a second dark bar beside the profile;
   *   · golaOrientOf reads a file's LONGEST axis, and his "Gola " entry holds
   *     a whole-kitchen export whose longest axis is its depth — so the rail
   *     read "horizontal", took that model, and squashed an entire kitchen
   *     into a 38 x 16mm strip. That is the second cluster of "pieces".
   * The vertical posts (buildGolaPosts) are THE gola now. The Room-level
   * horizontal RUN (buildGolaRuns) is untouched — it is opt-in per room and
   * still serves a horizontally-authored profile. */

  /* ── EQUIPMENT (2026-08-18 user: "taps hobs ovens hoods fridges dishwashers
   * — add it in Edit Module as Equipment … and we will use these, real time"):
   * each configured appliance renders as an ADDITIVE part on the module —
   * the technician's uploaded .glb mounts on the envelope (component-GLB
   * path), the box is the honest fallback. One emission point → orbit,
   * Present and the module-editor preview all show the same appliance. */
  {
    const _eqY = (absCenter) => y + absCenter - def.height / 2;   // floor-abs → part y
    const _wtTop = def.height + 0.04;                             // ≈ carcass + slab
    const _eqDefs = [
      /* SINKS (batch 11, 2026-09-29 user: "fill the sinks with real 3D like the other things"):
       * the bowl envelope hangs under the worktop line, its rim flush with the slab top; the
       * technician's .glb fits into it, else the box detail below draws a bowl. */
      ["eqSinks", "sinks", () => ({
        w: Math.min(0.78, Math.max(0.3, def.width - 0.06)), h: 0.19, d: Math.min(0.48, Math.max(0.3, def.depth - 0.12)),
        lz: 0.0, yAbs: _wtTop - 0.095 + 0.004,
      })],
      ["eqTaps", "taps", () => ({
        w: 0.06, h: 0.32, d: 0.06, lz: -(def.depth / 2 - 0.10), yAbs: _wtTop + 0.16,
      })],
      ["eqHobs", "hobs", () => ({
        w: Math.min(0.56, def.width - 0.06), h: 0.015, d: Math.min(0.49, def.depth - 0.10),
        lz: 0.02, yAbs: _wtTop + 0.008,
      })],
      ["eqOvens", "ovens", () => ({
        w: def.width - 0.004, h: 0.595, d: 0.06, lz: _fd - 0.02,
        yAbs: def.role === "cabinet-tall" || def.height > 1.4 ? 1.05 : def.height - 0.335,
      })],
      ["eqHoods", "hoods", () => ({
        w: def.width, h: 0.35, d: Math.min(0.50, def.depth), lz: 0,
        // on a wall unit the hood IS the module; on base/tall it hangs above
        yAbs: def.role === "cabinet-wall" ? def.height / 2 : def.height + 0.60 + 0.175,
      })],
      ["eqFridges", "fridges", () => ({
        w: def.width - 0.01, h: Math.max(0.5, def.height - 0.15), d: 0.06, lz: _fd - 0.02,
        yAbs: 0.10 + Math.max(0.5, def.height - 0.15) / 2,
      })],
      ["eqDishwashers", "dishwashers", () => ({
        w: def.width - 0.01, h: 0.76, d: 0.06, lz: _fd - 0.02, yAbs: 0.10 + 0.38,
      })],
    ];
    // richer BOX fallbacks when no .glb is attached — a tap reads as a tap,
    // an oven as an oven. With a .glb, ONE envelope part carries the model
    // (the GLB replaces it visually via the component path).
    const _eqDetail = {
      sinks: (e) => [
        { w: e.w, h: 0.012, d: e.d, dx: 0, dy: e.h - 0.006 },                                              // rim, flush with the slab
        { w: e.w - 0.03, h: 0.012, d: e.d - 0.03, dx: 0, dy: 0.006 },                                       // bowl floor
        { w: 0.012, h: e.h - 0.02, d: e.d - 0.03, dx: -(e.w / 2 - 0.02), dy: (e.h - 0.02) / 2 + 0.006 },    // bowl walls
        { w: 0.012, h: e.h - 0.02, d: e.d - 0.03, dx: (e.w / 2 - 0.02), dy: (e.h - 0.02) / 2 + 0.006 },
        { w: e.w - 0.03, h: e.h - 0.02, d: 0.012, dx: 0, dy: (e.h - 0.02) / 2 + 0.006, dz: -(e.d / 2 - 0.02) },
        { w: e.w - 0.03, h: e.h - 0.02, d: 0.012, dx: 0, dy: (e.h - 0.02) / 2 + 0.006, dz: (e.d / 2 - 0.02) },
      ],
      taps: (e) => [
        { w: 0.05, h: 0.03, d: 0.05, dx: 0, dy: 0.015 },                    // base
        { w: 0.024, h: 0.26, d: 0.024, dx: 0, dy: 0.145 },                  // riser
        { w: 0.024, h: 0.024, d: 0.16, dx: 0, dy: 0.27, dz: 0.07 },        // spout
      ],
      hoods: (e) => [
        { w: e.w, h: 0.06, d: e.d, dx: 0, dy: 0.03 },                       // canopy
        { w: Math.min(0.24, e.w / 2), h: e.h - 0.06, d: Math.min(0.30, e.d / 2), dx: 0, dy: 0.06 + (e.h - 0.06) / 2, dz: -e.d / 6 },  // chimney
      ],
      ovens: (e) => [
        { w: e.w, h: e.h, d: 0.02, dx: 0, dy: e.h / 2 },                    // face
        { w: e.w - 0.06, h: e.h - 0.16, d: 0.012, dx: 0, dy: (e.h - 0.16) / 2 + 0.04, dz: 0.014, glass: true },  // window
        { w: e.w - 0.10, h: 0.02, d: 0.03, dx: 0, dy: e.h - 0.05, dz: 0.02 },  // handle bar
      ],
      dishwashers: (e) => [
        { w: e.w, h: e.h, d: 0.02, dx: 0, dy: e.h / 2 },
        { w: e.w - 0.10, h: 0.02, d: 0.03, dx: 0, dy: e.h - 0.05, dz: 0.02 },
        { w: e.w - 0.04, h: 0.05, d: 0.012, dx: 0, dy: e.h - 0.10, dz: 0.014, glass: true },  // control strip
      ],
      fridges: (e) => [
        { w: e.w, h: e.h, d: 0.02, dx: 0, dy: e.h / 2 },
        { w: 0.025, h: Math.min(0.9, e.h * 0.5), d: 0.03, dx: e.w / 2 - 0.06, dy: e.h * 0.55, dz: 0.02 },  // handle
      ],
    };
    _eqDefs.forEach(([key, cat, mk]) => {
      const code = p.config && p.config[key];
      if (!code || code === "None" || def.role === "appliance") return;
      const entry = (((blob && blob.equipment) || {})[cat] || []).find((it) => it && it.code === code);
      if (!entry) return;
      const e = mk();
      const nm = def.name + " " + cat.replace(/s$/, "") + " equipment";
      const detail = !entry.glb && _eqDetail[cat];
      if (detail) {
        // dy from the envelope BOTTOM; dx along width; dz toward the front
        const yBot = e.yAbs - e.h / 2;
        detail(e).forEach((s) => {
          const c2 = _fxz(s.dx || 0, e.lz + (s.dz || 0));
          parts.push({
            kind: "box", role: "equipment", mat: "leg",
            w: s.w, h: s.h, d: s.d,
            x: c2.x, y: _eqY(yBot + (s.dy || 0)), z: c2.z,
            rotY: angle, name: nm,
            // the window / control strip flag rides along so a drawing can
            // show it as glass (elevation, 2026-09-12); renderers ignore it
            ...(s.glass ? { glass: true } : {}),
          });
        });
        return;
      }
      const c = _fxz(0, e.lz);
      parts.push({
        kind: "box", role: "equipment", mat: "leg",
        w: e.w, h: e.h, d: e.d,
        x: c.x, y: _eqY(e.yAbs), z: c.z, rotY: angle,
        name: nm,
        ...(entry.glb ? { model: entry.glb } : {}),
      });
    });
  }

  if (_standsOnPlinth) {
    // Kickboard (base AND tall, 2026-09-29): routes through the SAME cut engine (a
    // full-height wall cut removes its slice too). PARITY QUIRK KEPT: the kick sits at
    // ABSOLUTE height (world y 0) — an elevation override never moves it. Its height is
    // the platform plinth, the board applyPlinthRuns merges (a lone unit reads the same).
    _pushDeep(
      { x1: -(def.width - 0.020) / 2, z1: _fd, x2: (def.width - 0.020) / 2, z2: _fd + 0.02 },
      -_yBase, -_yBase + _plinthH, "leg", "kick", def.name + " kickboard"
    );
  }
  if (def.role === "cabinet-base" && isKitchenDef(def)) {
    // (a wardrobe / bed / sofa whose category mapped to the base role carries
    // neither a kickboard nor a counter — engine/families, 2026-09-29)
    // The countertop routes through the SAME cut engine (it follows top cuts).
    // PARITY QUIRK KEPT: the counter sits at an ABSOLUTE height (def.height+0.019).
    /* SLAB SPEC (2026-08-15): thickness + front overhang now come from the
     * technician material (Cabinet Technician -> Worktops). No material, or a
     * material without the fields, resolves to the engine defaults 38mm / 20mm
     * — the exact literals this line used to carry, so untouched catalogs are
     * byte-identical. applyWorktopRuns copies these dims onto the run slab, so
     * a merged run inherits the spec for free. */
    const _wtSpec = surfaceSpecOf(surfaceMaterial(blob, "worktop", (p.config || {}).countertop), "worktop", null);
    const _wtT = _wtSpec.thicknessMm / 1000;
    const _wtOh = _wtSpec.overhangFrontMm / 1000;
    const slabD = def.depth + _wtOh;
    _pushDeep(
      { x1: -def.width / 2, z1: (_wtOh / 2) - slabD / 2, x2: def.width / 2, z2: (_wtOh / 2) + slabD / 2 },
      def.height - _yBase, def.height + _wtT - _yBase, "counter", "counter", def.name + " counter"
    );
    /* BACKSPLASH: emitted as a RUN by applySplashRuns, not per module — the
     * per-module panel produced one abutting 550mm slab per cabinet, and its
     * height was hardcoded. See engine/surfaces.js. */
  }

  /* REAL GOLA CUT ON UPLOADS (2026-08-24 user: "make the gola like a real
   * thing — whatever it touches gets an 18×18 hole, like a knife through
   * butter" — the module's 3D must NOT swap away). The spec carries:
   *  · bodyEnvelope — where the carcass BOX would sit un-cut, so the GLB fit
   *    has an anchor even when the notch band-split the procedural body;
   *  · golaNotchPrisms — the notch volumes in the ENVELOPE's local frame
   *    (x lateral centre-0, y centre-0, z centre-0/+front), boolean-subtracted
   *    from the fitted GLB's carcass meshes (doors are never cut). */
  /* The CSG prism set = the SAME standard 18×18 pockets, as volumes for the
   * uploaded model (2026-08-24f): the upload renders untouched and only these
   * prisms are boolean-subtracted from its carcass meshes. */
  const _gnPr = [];
  /* AUTHORED parametric GOLA CUTS (2026-08-24m Cut → GOLA Cut): the user's
   * own mm-defined cut features become CSG volumes for uploaded models —
   * the ONLY gola cutting there is (2026-08-24o: no automatic carving). */
  ((p.config && p.config.cuts) || []).forEach((c) => {
    if (!c || c.kind !== "gola" || c.enabled === false) return;
    if (_autoRebate) return;                       // superseded by the automatic pocket
    const r = golaCutRectM(c, def);
    if (!(r.x2 - r.x1 > 0) || !(r.y2 - r.y1 > 0)) return;
    _gnPr.push({
      w: r.x2 - r.x1, h: (r.y2 - r.y1) + 0.002, d: r.z2 - r.z1,
      x: (r.x1 + r.x2) / 2, y: (r.y1 + r.y2) / 2 - _H / 2, z: (r.z1 + r.z2) / 2,
      golaCut: true,
    });
  });
  /* …and the DERIVED rebate as its OWN list (2026-08-26). It is NOT a CSG
   * prism: an uploaded carcass gets the demo's panel-shortening notch instead
   * (glbNotch.shortenPanelsForGola — "wrap the panel in a pivot at its back
   * edge and scale the depth"), because a boolean only removes what it happens
   * to intersect and measured meshesCut: 0 on the user's own left gable. The
   * volumes are identical rectangles in the envelope frame. */
  const _gnRb = [];
  golaNotchCuts(def, _gnAll, { depthM: _golaDepM }).forEach((c) => {
    const xs = c.points.map((q) => q[0]), zs = c.points.map((q) => q[1]);
    const x1 = Math.min(...xs), x2 = Math.max(...xs);
    const z1 = Math.min(...zs), z2 = Math.max(...zs);
    _gnRb.push({
      w: x2 - x1, h: _H + 0.002, d: z2 - z1,
      x: (x1 + x2) / 2, y: 0, z: (z1 + z2) / 2,
      // how much gable comes off its OWN measured front — the one number the
      // panel notch needs, and the one his SketchUp cut actually removes
      cutM: _golaDepM > 0 ? _golaDepM : GOLA_NOTCH_MM / 1000,
    });
  });
  return {
    moduleId: p.id, name: def.name, role: def.role, angle, parts,
    // THE instance's hinge side ("L"/"R"/"N") — an uploaded model's own door
    // hangs on this side when it is the only leaf in its row (2026-09-15).
    hinge: _hinge,
    // GLB modules read this to hide their model's OWN front bucket too — the
    // appliance face owns the front everywhere (orbit, Present, preview).
    ...(_eqOwnsFront ? { eqOwnsFront: true } : {}),
    /* BODY ENVELOPE — ALWAYS (2026-08-24): where the carcass BOX sits when
     * nothing cuts it. A cut band-splits that box into shapeBands, and every
     * consumer that anchored on "the box carcass part" then found nothing:
     * the GLB fit lost its anchor, the gola posts lost their span, and
     * Present's pose builder skipped the module ENTIRELY — a cut module was
     * invisible in the render while the 3D showed it. */
    bodyEnvelope: {
      w: _cW, h: _cH, d: _cD,
      x: cx, y: _yBase + _H / 2, z: cz, rotY: angle,
    },
    /* THE UPLOAD'S OWN SIZE (2026-08-26 user: "make sure when I upload gola
     * glb and module glb they are correct shown AS I UPLOAD THEM"). The
     * procedural carcass box is deliberately smaller than the module — 8mm on
     * width/height, 25mm on depth — and an uploaded model was being fitted
     * into THAT, so a 600 x 2201 x 597 file rendered at 592 x 2193 x 572: a 4%
     * squeeze on depth. It also put every derived plane 12mm out, which is
     * what made the gola notch land 8.5mm instead of 20. A GLB is now fitted
     * to the module's REAL dimensions, concentric with the body. */
    modelEnvelope: {
      w: def.width, h: def.height, d: def.depth,
      x: cx, y: _yBase + _H / 2, z: cz, rotY: angle,
    },
    ...(_gnPr.length ? { golaNotchPrisms: _gnPr } : {}),
    ...(_gnRb.length ? { golaRebates: _gnRb } : {}),
  };
}

/** buildCabinetSpecs(state, lib): the buildSofa (~32804) placement pass — kitchen path —
 *  as pure specs. Wall placements route through cabinetWorldPos (v0.36 ONE-ORIGIN);
 *  wall-less placements use the sequential row layout from getCabinetStartX. */
/** the across-the-wall width of a placement's chosen gola profile — the one
 *  measurement that decides single vs double sided (golaClearance). */
const _acrossOf = (blob) => (q) =>
  golaGeomOf(blob, golaProfileById(blob, ((q && q.config) || {}).gola)).acrossM;

/** cornerOf(placement, def, "L"|"R") → the world XZ of that side's FRONT
 *  corner. THE adjacency anchor for the automatic rebate: on a straight run
 *  two facing corners sit 30mm apart, at an inside corner they are the same
 *  point — one distance test, no wall ids, so L-kitchens and islands work
 *  exactly like a straight wall (2026-08-26 corner mandate). */
const _cornerOf = (walls, lib) => (q, qd, side) => {
  if (!q || !qd) return null;
  let cx, cz, ang = 0;
  if (q.wallId) {
    const pos = cabinetWorldPos(q, qd, walls);
    if (!pos) return null;
    cx = pos.cx; cz = pos.cz; ang = pos.angle || 0;
  } else if (q.x != null && q.z != null) {
    cx = q.x; cz = q.z;                                  // island / free placement
  } else {
    return null;
  }
  const ovr = (q.config && q.config.position) || {};
  if (ovr.angle) ang += (ovr.angle * Math.PI) / 180;
  if (ovr.outset) { cx += Math.sin(ang) * (ovr.outset / 1000); cz += Math.cos(ang) * (ovr.outset / 1000); }
  const ca = Math.cos(ang), sa = Math.sin(ang);
  const lx = (side === "L" ? -1 : 1) * ((qd.width || 0) / 2);
  const lz = (qd.depth || 0) / 2;                        // the FRONT face
  return { x: cx + ca * lx + sa * lz, z: cz - sa * lx + ca * lz };
};

export function buildCabinetSpecs(state, lib, blob) {
  const specs = [];
  if (!state) return specs;
  const walls = state.walls || [];
  const placements = state.placements || [];
  let cursorX = getCabinetStartX(walls, placements, lib);
  placements.forEach((p) => {
    const def = effDef(p, lib);
    if (!def) return;
    // Non-back wall cabinet: cabinetPartsSpec uses cabinetWorldPos internally
    if (p.wallId) { // v0.36 ONE-ORIGIN: back wall through the engine frame too
      const pos = cabinetWorldPos(p, def, walls);
      const computedCX = pos ? pos.cx : 0;
      specs.push(cabinetPartsSpec(p, def, computedCX, walls, blob,
        { golaNotchSides: golaNotchSides(p, def, placements, (q) => effDef(q, lib)),
          golaRebateSides: golaRebateSides(p, def, placements, (q) => effDef(q, lib), _acrossOf(blob), _cornerOf(walls, lib)) }));
      return;
    }
    let centerX;
    if (p.absLeft != null) {
      centerX = getCabinetStartX(walls, placements, lib) + p.absLeft + def.width / 2;
      cursorX = getCabinetStartX(walls, placements, lib) + p.absLeft + def.width;
    } else {
      const clrL = p.clrLeft || 0;
      const clrR = p.clrRight || 0;
      cursorX += clrL;
      centerX = cursorX + def.width / 2;
      cursorX += def.width + clrR;
    }
    specs.push(cabinetPartsSpec(p, def, centerX, walls, blob,
      { golaNotchSides: golaNotchSides(p, def, placements, (q) => effDef(q, lib)),
        golaRebateSides: golaRebateSides(p, def, placements, (q) => effDef(q, lib), _acrossOf(blob), _cornerOf(walls, lib)) }));
  });
  return specs;
}

/* ── WORKTOP RUNS (Phase 4): merge adjacent per-cabinet counter slabs into ONE
 * continuous run slab per group + sheet-length seam grooves. Mutates `specs`
 * in place (member counter parts removed, run specs appended) — called by
 * buildCabinetMeshes AFTER the model-resolver pass so GLB/component-topped
 * instances can be excluded (they stay solo, byte-identical to before). ── */
/* ── GOLA 3D RUNS (2026-08-15): the technician's uploaded profile, run along
 * the base modules as REAL geometry. Derived from the room pick, so it
 * re-flows whenever a cabinet moves; the customer's manual height / inset /
 * profile-height / snap live in kitchenWizard.golaAdjust (engine/golaRuns).
 * Each run becomes ONE spec whose single part carries `model`, so the scene
 * fits the real GLB onto the run box (component-GLB path) and Present renders
 * the identical spec. */
export function applyGolaRuns(specs, state, lib, blob) {
  const placements = (state && state.placements) || [];
  const byId = new Map(specs.map((s) => [s.moduleId, s]));
  // geometry of a placed module in ITS wall's axis — taken from the module's
  // OWN carcass part so cuts, angles and outsets are respected. botM rides
  // along for the vertical posts (they span the real carcass, plinth to top).
  const geomOf = (p, def) => {
    const spec = byId.get(p.id);
    if (!spec) return null;
    // a module with gola-cut CSG prisms band-splits its carcass — the
    // bodyEnvelope is the SAME anchor (2026-08-24o "when I gola cut, the
    // gola disappears": geomOf found no box and the posts vanished; a
    // partial-height cut even found the WRONG band box).
    const body = spec.bodyEnvelope
      || spec.parts.find((pt) => pt && pt.kind === "box" && (pt.role === "carcass" || !pt.role));
    if (!body) return null;
    const ang = spec.angle || 0;
    const cos = Math.cos(ang), sin = Math.sin(ang);
    const s = body.x * cos - body.z * sin;
    const q = body.x * sin + body.z * cos;
    /* THE PROFILE STOPS WHERE THE MODULE ENDS (2026-08-24 user: "the gola
     * should stop at the end of the module, not go down to the plinth").
     * The engine's carcass box spans the FULL def height from the floor and
     * the kickboard is drawn in front of its bottom 80mm (an engine parity
     * quirk) — so a post spanning the carcass ran down BEHIND the plinth to
     * the floor. The plinth board's top is the real bottom of the cabinet
     * body; modules without one (wall / tall) are unchanged. */
    const kick = spec.parts.find((pt) => pt && pt.role === "kick");
    const bodyBot = body.y - body.h / 2;
    const kickTop = kick ? kick.y + kick.h / 2 : null;
    /* THE DOOR'S LOWER EDGE (batch 10): the lowest front of the module — a door leaf's
     * bottom (pivot y − leafH/2) or a drawer front's bottom — so the post seat is the
     * engine's own number on every module, uploaded or procedural. */
    let frontBotM = null;
    spec.parts.forEach((pt) => {
      if (!pt) return;
      let b = null;
      if (pt.kind === "doorPivot" && Number.isFinite(pt.leafH)) b = pt.y - pt.leafH / 2;
      else if (pt.kind === "drawerUnit" && Array.isArray(pt.children)) {
        pt.children.forEach((ch) => { if (ch && / drawer front$/.test(String(ch.name || "")) && Number.isFinite(ch.h)) { const v = pt.y + (ch.y || 0) - ch.h / 2; b = b == null ? v : Math.min(b, v); } });
      } else if (pt.kind === "box" && pt.role === "front" && Number.isFinite(pt.h)) b = pt.y - pt.h / 2;
      if (b != null && Number.isFinite(b)) frontBotM = frontBotM == null ? b : Math.min(frontBotM, b);
    });
    return {
      s, q, angle: ang, w: def.width,
      topM: (def.height || 0.78),                    // NOMINAL top (run banding key)
      topRealM: body.y + body.h / 2,                 // ACTUAL top (elevation override)
      botM: kickTop != null ? Math.max(bodyBot, kickTop) : bodyBot,
      kickTopM: kickTop,
      frontBotM,
      depthM: def.depth || 0.575,
    };
  };
  const runs = buildGolaRuns({
    placements, lib, blob,
    wizard: (state && state.kitchenWizard) || null,
    effDef: (p, l) => effDef(p, l),
    geomOf,
  });
  /* VERTICAL POSTS (2026-08-18): per-module Left/Right/double placement from
   * Edit Module (config.golaPos), stitched between neighbours by the engine.
   * The part is built HORIZONTAL (length on local X, section h×d) and stood
   * up with rotZ, so the uploaded profile GLB keeps its real cross-section —
   * the same fit the horizontal run uses, just rotated.
   * Built BEFORE the runs (2026-08-22 SketchUp-fit mandate): the posts are
   * the CONTINUOUS members of the assembly, and each horizontal run is
   * TRIMMED around every post footprint it crosses — clean butt joints on one
   * coplanar face, never the old overlap. */
  const posts = buildGolaPosts({
    placements, lib, blob,
    effDef: (p, l) => effDef(p, l),
    geomOf,
  });
  // post footprints along their wall axis: [a, b] + angle, for run trimming
  const postSpans = posts.map((po) => {
    // ONE seat rule (2026-08-26): the trim footprint is the seat's own ACROSS
    // span, never a second local copy of the section maths.
    const s0 = golaPostSeat(po, blob);
    return { angle: po.angle || 0, a: s0.sIn - s0.dSec / 2 - 0.0005, b: s0.sIn + s0.dSec / 2 + 0.0005 };
  });
  runs.forEach((r) => {
    const cos = Math.cos(r.angle || 0), sin = Math.sin(r.angle || 0);
    // subtract every crossing post footprint → the run becomes 1..n segments
    // butting cleanly against the continuous vertical members (render-only:
    // golaInfo.lengthM and the returned run objects — pricing — unchanged).
    let segs = [[r.s0, r.s1]];
    postSpans.forEach((ps) => {
      if (Math.abs(((ps.angle - (r.angle || 0)) + Math.PI) % (2 * Math.PI) - Math.PI) > 1e-6) return;
      const next = [];
      segs.forEach(([a, b]) => {
        if (ps.b <= a + 1e-9 || ps.a >= b - 1e-9) { next.push([a, b]); return; }
        if (ps.a > a + 0.008) next.push([a, ps.a]);
        if (ps.b < b - 0.008) next.push([ps.b, b]);
      });
      segs = next;
    });
    const parts = segs.map(([a, b], k) => {
      const sMid = (a + b) / 2;
      return {
        kind: "box", role: "gola", mat: "leg",
        w: b - a, h: r.hM, d: r.dM,
        x: cos * sMid + sin * r.q, y: r.yM, z: -sin * sMid + cos * r.q,
        // CUT ANCHOR (2026-08-22): mesh pressed to the TOP + FRONT envelope
        // faces — any profile size hangs from the same carcass-top line.
        snapY: "max", snapZ: "max",
        rotY: r.angle || 0, model: r.model, name: k ? "gola run seg" : "gola run",
      };
    });
    if (!parts.length) return; // fully consumed by posts — nothing to show
    specs.push({
      moduleId: r.id,
      name: "Gola · " + r.profile,
      role: "gola-run",
      angle: r.angle || 0,
      // colour anchor (2026-08-24): only the vertical posts carried one, so a
      // Gola Colour pick repainted the posts and left the horizontal rail in
      // the default grey — it read as half the gola vanishing.
      visFrom: (r.members && r.members[0]) || undefined,
      golaInfo: { profile: r.profile, lengthM: r.lengthM, members: r.members.slice(), snap: !!r.snap },
      parts,
    });
  });
  posts.forEach((po) => {
    const cos = Math.cos(po.angle || 0), sin = Math.sin(po.angle || 0);
    /* GOLA_RECESS_ANCHOR v2 + SMART SEAT + TECHNICIAN FIT — the entire seat
     * (section, rotation, lateral offset, depth into the authored pocket,
     * vertical span) now comes from golaPostSeat(), THE one gola geometry
     * rule shared with elevation and plan (2026-08-23 mandate: one source of
     * truth — never three view-local versions). History of the formulas
     * lives with golaPostSeat in engine/golaRuns.js. */
    const seat = golaPostSeat(po, blob, { rotDeg: po.rotDeg || 0 });
    const wSec = seat.wSec, dSec = seat.dSec;
    const topEff = po.topM;
    const botEff = po.botM;
    const len = seat.lenM;
    const _secRot = seat.secRot;
    const sIn = seat.sIn;
    const q = seat.q;
    specs.push({
      moduleId: po.id,
      name: "Gola · " + po.profile + " (vertical)",
      role: "gola-run",
      angle: po.angle || 0,
      visFrom: po.members[0],                          // gola colour follows its module
      golaInfo: {
        profile: po.profile, vertical: true, members: po.members.slice(),
        lengthM: Math.round(len * 1000) / 1000,
        ...(po.fixedTop ? { fixedTop: true } : {}),
        ...(po.toFloor ? { toFloor: true } : {}),
        botM: botEff,                                    // the engine's seat — the aligner never lowers a post under it
      },
      parts: [{
        kind: "box", role: "gola", mat: "leg",
        w: len, h: dSec, d: wSec,
        x: cos * sIn + sin * q, y: (botEff + topEff) / 2, z: -sin * sIn + cos * q,
        // FRONT FACE FORWARD (2026-08-22 user: "now just rotate the gola to
        // be with front face"): the C-channel's closed side faces the room,
        // the opening swallows backward — fitGlbProfile's area-centroid flip
        // lands any authoring orientation correctly.
        /* THE CHANNEL FACES THE ROOM (2026-08-26 user: "gola should face me
         * not behind"): the open side of the C is the finger grip, so it must
         * point at the person standing in the kitchen, not into the carcass.
         * config.golaRot turns it further if a profile is authored differently. */
        rotY: po.angle || 0, rotZ: Math.PI / 2, model: seat.model || po.model, openTo: "front",
        ...(_secRot ? { secRot: _secRot } : {}),
        /* MIRROR ON THE LEFT (2026-08-26, the demo's mirrorX = -1): a
         * single-sided profile carries its mounting lip on ONE edge, so the
         * left-hand instance must be a true geometric MIRROR of the right —
         * the old 180° section rotation flipped the depth direction with it
         * and pointed the channel backwards. */
        ...(seat.mirror ? { mirror: true } : {}),
        /* FRONT FLUSH: the envelope now IS the profile's real section, so the
         * mesh fills it exactly and only the depth anchor matters — the
         * profile's face lands on the gable front (the door-back plane) for
         * any profile size, which is the demo's p0z. */
        snapZ: "max", name: "gola post",
      }],
    });
  });
  return runs.concat(posts.map((po) => ({
    ...po, post: true,
    lengthM: Math.round(Math.max(0.05, po.topM - po.botM) * 1000) / 1000,
  })));
}

/* ── BACKSPLASH RUNS (2026-08-15) ──────────────────────────────────────────
 * The splash used to be a per-module 12x550mm panel, so an 8-cabinet wall
 * rendered 8 abutting slabs and the height could not be changed. It is now a
 * RUN, exactly like the worktop: one panel per contiguous band of modules
 * sharing the material, sitting ON the slab below it, with thickness and
 * height from the technician material (Room can override). Emitted as its own
 * spec so orbit, the module editor and Present all build the same geometry. */
/* ── ONE SURFACE AXIS (2026-08-18) ─────────────────────────────────────────
 * s = distance from the WALL START, positive along the wall direction — the
 * exact axis the elevation draws and the quote prices. The 3D used a raw
 * world projection before, which disagreed on offset/reversed walls: an
 * elevation trim landed shifted or mirrored in 3D. wallAxisFor converts the
 * rotated-frame world projection into that axis and back. */
function wallAxisFor(walls, wallId, ang) {
  const w = (walls || []).find((x) => x && x.id === wallId);
  const cos = Math.cos(ang || 0), sin = Math.sin(ang || 0);
  if (!w || !Array.isArray(w.start) || !Array.isArray(w.end)) {
    return { toRel: (sw) => sw, toWorld: (sr) => sr, cos, sin };
  }
  const sOf = (x, z) => x * cos - z * sin;
  const s0w = sOf(w.start[0], w.start[1]);
  // does the world s-axis run WITH the wall direction or against it?
  const sign = (sOf(w.end[0], w.end[1]) - s0w) >= 0 ? 1 : -1;
  return {
    toRel: (sw) => sign * (sw - s0w),
    toWorld: (sr) => s0w + sign * sr,
    cos, sin,
  };
}

export function applySplashRuns(specs, state, lib, blob) {
  const byId = new Map(specs.map((s) => [s.moduleId, s]));
  const { worktops, splashes } = buildSurfaces({
    placements: (state && state.placements) || [],
    lib, blob, wizard: (state && state.kitchenWizard) || null,
    effDef: (p, l) => effDef(p, l),
    walls: (state && state.walls) || [],
    openings: (state && state.wallOpenings) || [],
    geomOf: (p, def) => {
      const spec = byId.get(p.id);
      if (!spec) return null;
      const body = spec.parts.find((pt) => pt && pt.kind === "box" && (pt.role === "carcass" || !pt.role));
      if (!body) return null;
      const ang = spec.angle || 0;
      const ax = wallAxisFor((state && state.walls) || [], p.wallId, ang);
      return {
        s: ax.toRel(body.x * ax.cos - body.z * ax.sin),
        q: body.x * ax.sin + body.z * ax.cos,
        angle: ang, w: def.width,
        topM: def.height, depthM: def.depth,
      };
    },
  });
  splashes.forEach((sp, i) => {
    const ax = wallAxisFor((state && state.walls) || [], sp.wallId, sp.angle || 0);
    const cos = ax.cos, sin = ax.sin;
    const sMid = ax.toWorld((sp.s0 + sp.s1) / 2);
    // the panel hugs the WALL side of the cabinet band: back face of the
    // carcass, its own thickness deep, starting on top of the slab below
    const qBack = sp.q - sp.depthM / 2 + sp.thicknessM / 2;
    const y0 = worktopTopFor(sp, worktops);
    specs.push({
      moduleId: "__splash__" + i,
      name: "Backsplash · " + sp.material,
      role: "splash-run",
      angle: sp.angle || 0,
      visFrom: sp.members[0],                          // finish follows its first member
      surfaceKey: sp.key,
      splashInfo: { material: sp.material, lengthM: sp.lengthM, heightMm: Math.round(sp.heightM * 1000), members: sp.members.slice() },
      parts: [{
        kind: "box", role: "backsplash", mat: "body",
        w: sp.s1 - sp.s0, h: sp.heightM, d: sp.thicknessM,
        x: cos * sMid + sin * qBack, y: y0 + sp.heightM / 2, z: -sin * sMid + cos * qBack,
        rotY: sp.angle || 0, name: "backsplash run",
      }],
    });
  });
  return splashes;
}

export function applyWorktopRuns(specs, state, lib, blob, resById) {
  const placements = (state && state.placements) || [];
  const byId = new Map(specs.map((s) => [s.moduleId, s]));
  const meta = new Map();                              // id → {spec, part, cos, sin, q, solo}
  placements.forEach((p) => {
    if (!p || !p.wallId) return;
    const def = effDef(p, lib);
    if (!def || def.role !== "cabinet-base") return;
    const spec = byId.get(p.id);
    if (!spec) return;
    const ci = spec.parts.findIndex((pt) => pt && pt.kind === "box" && pt.role === "counter");
    if (ci < 0) return;                                // clipped / bespoke counter → solo
    const res = resById && resById[p.id];
    const cfg = p.config || {};
    const solo =
      (cutsOf(cfg, def).length > 0) || (cfg.topCut && cfg.topCut.enabled) ||   // cut slabs stay clipped per-cabinet
      // 2026-08-05 inversion: a GLB only owns its top when the analysis FOUND
      // one — open-topped manufacturer carcasses join runs like anyone else
      !!(res && ((res.baseGlb && res.glbPlan && res.glbPlan.ownsWorktop) || (res.componentGlbs || {}).worktop));
    const part = spec.parts[ci];
    const cos = Math.cos(spec.angle || 0), sin = Math.sin(spec.angle || 0);
    const q = part.x * sin + part.z * cos;
    meta.set(p.id, { spec, part, cos, sin, q, solo, def });
  });
  /* ONE ORACLE (2026-08-18): grouping, mixed-size banding, corner butt-joints
   * and elevation overrides all come from buildSurfaces — the 3D renders the
   * SAME runs the elevation and the quantities see. */
  const oracle = buildSurfaces({
    placements, lib, blob,
    wizard: (state && state.kitchenWizard) || null,
    effDef: (p, l) => effDef(p, l),
    walls: (state && state.walls) || [],
    openings: (state && state.wallOpenings) || [],
    soloOf: (p) => { const m = meta.get(p.id); return !!(m && m.solo); },
    geomOf: (p, def) => {
      const m = meta.get(p.id);
      if (!m) return null;
      const ang = m.spec.angle || 0;
      const ax = wallAxisFor((state && state.walls) || [], p.wallId, ang);
      return {
        s: ax.toRel(m.part.x * m.cos - m.part.z * m.sin),
        q: m.q, angle: ang, w: m.part.w,
        topM: m.part.y - m.part.h / 2,                 // slab BOTTOM (band identity)
        depthM: def.depth,                             // carcass depth (oracle adds the overhang)
      };
    },
  });
  const runs = [];
  oracle.worktops.forEach((run, i) => {
    // singles keep the module's OWN counter part byte-identically — a run slab
    // is emitted only when something merged or a corner/override adjusted it
    const adjusted = (run.cornerJoints || 0) > 0 || (run.ends && (run.ends.s0 === "corner" || run.ends.s1 === "corner")) || !run.auto;
    if (run.members.length < 2 && !adjusted) return;
    const metas = run.members.map((id) => meta.get(id)).filter(Boolean);
    if (!metas.length) return;
    // the DEEPEST member's part drives slab depth + wall distance (one
    // straight front line over mixed depths — the CAD default)
    let deep = metas[0];
    metas.forEach((m) => { if (m.part.d > deep.part.d + 1e-9) deep = m; });
    metas.forEach((m) => {
      const ci = m.spec.parts.indexOf(m.part);
      if (ci >= 0) m.spec.parts.splice(ci, 1);
    });
    const m0 = metas[0];
    const ang0 = m0.spec.angle || 0;
    const ax = wallAxisFor((state && state.walls) || [], run.wallId, ang0);
    const cos = ax.cos, sin = ax.sin;
    const part = deep.part, q0 = deep.q;
    /* OVERRIDDEN DIMS RENDER (2026-08-31 user: "Depth / Thickness / Side
     * overhang are NOT working"): the slab took the member part's h/d, so
     * only s0/s1 ever reached the mesh — the panel numbers moved, the 3D
     * did not. A run whose RAW override carries the field now renders the
     * oracle's number: thickness grows UP off the fixed slab bottom, depth
     * grows FORWARD off the fixed back edge (point 0), and the side
     * overhang extends the FREE ends only. Fields the override does not
     * carry keep the member part verbatim — auto runs stay byte-identical. */
    const _ovStore = ((state && state.kitchenWizard) || {}).surfaceOverrides || null;
    const _ov = resolveOverride(_ovStore, run.key, run.members) || {};
    const T = _ov.thicknessMm > 0 ? run.thicknessM : part.h;
    const B = _ov.overhangBackMm > 0 ? (run.overhangBackM || 0) : 0;
    const D = (_ov.overhangFrontMm != null || _ov.overhangBackMm != null) ? run.depthM : part.d;
    // Y slide (2026-08-31): the whole slab moves forward/back in mm
    const q = q0 - part.d / 2 - B + D / 2 + (run.qOffM || 0);
    const yC = part.y - part.h / 2 + T / 2;        // slab bottom stays on the carcass
    // PER-END wings (2026-08-31): each free end wears ITS OWN overhang —
    // rendered only when the RAW override carries a side value at all
    const _osOn = _ov.overhangSideMm != null || _ov.overhangSideLMm != null || _ov.overhangSideRMm != null;
    const e0 = _osOn && run.ends && run.ends.s0 === "free" ? (run.overhangSideLM || 0) : 0;
    const e1 = _osOn && run.ends && run.ends.s1 === "free" ? (run.overhangSideRM || 0) : 0;
    const sMid = ax.toWorld((run.s0 - e0 + run.s1 + e1) / 2);
    // CORNER BUTT LINE (2026-08-18 user mandate: "a small line so you see
    // which slab is which — like a real professional L-connection"): the
    // BUTTED run's corner end gets the same groove a factory joint gets.
    const seamsW = run.seams.map((sp) => ax.toWorld(sp));
    if (run.cornerButt === "s0") seamsW.push(ax.toWorld(run.s0 + 0.002));
    if (run.cornerButt === "s1") seamsW.push(ax.toWorld(run.s1 - 0.002));
    specs.push({
      moduleId: "__wtrun__" + i,
      name: "Worktop run " + (i + 1),
      role: "worktop-run",
      angle: ang0,
      visFrom: run.members[0],                         // run tint follows its first member's pick
      surfaceKey: run.key,
      runInfo: { lengthM: run.lengthM, seams: run.seams.length, members: run.members.slice(), joints: (run.cornerJoints || 0) + (run.stepJoints || 0) },
      parts: [
        {
          kind: "box", role: "counter", mat: part.mat,
          w: (run.s1 + e1) - (run.s0 - e0), h: T, d: D,
          x: cos * sMid + sin * q, y: yC, z: -sin * sMid + cos * q,
          rotY: ang0, name: "Worktop run " + (i + 1),
        },
        ...seamsW.map((sp, k) => ({
          kind: "box", role: "seam", mat: "leg",
          w: 0.004, h: T + 0.002, d: D + 0.002,
          x: cos * sp + sin * q, y: yC, z: -sin * sp + cos * q,
          rotY: ang0, name: "worktop seam " + (k + 1),
        })),
      ],
    });
    runs.push(run);
  });
  return runs;
}

/* ── PLINTH RUNS (batch 10, 2026-09-29 user: "plinth when connected — the plinth must
 * become ONE until you meet a gola; if no gola, then it is connected, one line, all").
 * Adjacent floor-standing modules on a wall share ONE continuous kickboard: the members'
 * own kick parts are flagged `merged` (never rendered) and one "plinth-run" spec per
 * contiguous group is appended, wearing the first member's plinth finish (visFrom). A
 * vertical gola post that reaches INTO the plinth zone (Down to floor) cuts the run around
 * its across-span, exactly like the horizontal gola rail is trimmed around posts; a post
 * that stops at the door bottom leaves the plinth running under it. Grouping is by wall,
 * angle, face, finish key and kick plane; the tolerance bridges the 10 mm insets + the
 * 30 mm gola clearance. Singles keep their own kick byte-identically. Mutates `specs`. ── */
export const PLINTH_RUN_GAP_M = 0.06;
export function applyPlinthRuns(specs, state, lib, blob, opts) {
  const placements = (state && state.placements) || [];
  const byId = new Map(specs.map((s) => [s.moduleId, s]));
  const keyOf = (opts && typeof opts.keyOf === "function") ? opts.keyOf : () => "";
  const golaPosts = ((opts && opts.golaPosts) || []).filter((po) => po && po.post);
  const meta = [];
  placements.forEach((p) => {
    if (!p || !p.wallId || p.island) return;
    const def = effDef(p, lib);
    if (!def || (def.role !== "cabinet-base" && def.role !== "cabinet-tall")) return;   // talls stand on the plinth too (2026-09-29)
    const spec = byId.get(p.id);
    if (!spec || !Array.isArray(spec.parts)) return;
    const part = spec.parts.find((pt) => pt && pt.kind === "box" && pt.role === "kick" && !pt.merged);
    if (!part || part.holes || !(part.w > 0.05)) return;             // clipped / bespoke kick → solo
    if (cutsOf(p.config || {}, def).some((c) => c && !c.golaNotch)) return;   // an authored cut keeps its own kick
    const ang = spec.angle || 0;
    const cos = Math.cos(ang), sin = Math.sin(ang);
    const s = part.x * cos - part.z * sin;
    const q = part.x * sin + part.z * cos;
    meta.push({
      id: p.id, spec, part, ang, cos, sin, q,
      s0: s - part.w / 2, s1: s + part.w / 2,
      y: part.y, h: part.h, d: part.d, mat: part.mat,
      wallId: p.wallId, face: p.faceSign === -1 ? -1 : 1, key: String(keyOf(p) || ""),
    });
  });
  if (meta.length < 2) return [];
  const groups = new Map();
  meta.forEach((m) => {
    const k = [m.wallId, Math.round(m.ang * 1000), m.face, m.key, Math.round(m.q * 200), Math.round(m.y * 1000), Math.round(m.h * 1000)].join("|");
    if (!groups.has(k)) groups.set(k, []);
    groups.get(k).push(m);
  });
  const runs = [];
  groups.forEach((arr) => {
    arr.sort((a, b) => a.s0 - b.s0);
    let cur = null;
    const flush = () => { if (cur) runs.push(cur); cur = null; };
    arr.forEach((m) => {
      if (cur && m.s0 - cur.s1 <= PLINTH_RUN_GAP_M + 1e-6) { cur.s1 = Math.max(cur.s1, m.s1); cur.members.push(m); }
      else { flush(); cur = { ang: m.ang, cos: m.cos, sin: m.sin, q: m.q, y: m.y, h: m.h, d: m.d, mat: m.mat, wallId: m.wallId, s0: m.s0, s1: m.s1, members: [m] }; }
    });
    flush();
  });
  const angDiff = (a, b) => Math.abs(((a - b) + Math.PI) % (2 * Math.PI) - Math.PI);
  /* BOARD HEIGHT (batch 11, 2026-09-29 user: "the plinth is not correct and not connected with the
   * cabinet"): the engine kick is an 80 mm parity box, but every real module stands on the platform
   * plinth (rulePlinthH, 100 mm) — his uploads show their feet and levelling plates between an
   * 80 mm board and the carcass. The shared board is the platform height, from the floor up, so it
   * meets the carcass bottom. */
  let plinthH = 0.1;
  try { plinthH = ruleVal("PLINTH_H", (state && state.kitchenWizard) || null) || 0.1; } catch (e) { plinthH = 0.1; }
  /* BOARD DEPTH (2026-09-29 user: "in 3D the plinth is not connected with the module and it is
   * FORWARD, not where it belongs"): the engine kick is drawn 8 mm PROUD of the door face (the
   * iframe parity quirk), while a real toe-kick — and every uploaded module's own strip (his
   * BU-01: 39 mm behind the door face) — sits back in the recess. The shared board takes the
   * module's plinth recess (cfg.plinth.recess, else 40 mm) behind the door face, 20 mm thick. */
  runs.forEach((r) => {
    if (r.members.length < 2) return;
    const h = Math.max(r.h, plinthH);
    const bot = Math.min(r.y - r.h / 2, 0);
    r.h = h; r.y = bot + h / 2;
    const doorFace = r.q + r.d / 2 - 0.008;                            // the kick's front is 8 mm proud of the doors
    let recess = 0.04;
    r.members.forEach((m) => {
      const p = placements.find((x) => x && x.id === m.id);
      const rc = p && p.config && p.config.plinth && Number(p.config.plinth.recess);
      if (rc > 0) recess = Math.max(recess, Math.min(0.1, rc / 1000));
    });
    r.d = 0.02;
    r.qDoor = doorFace;
    r.q = doorFace - recess - r.d / 2;
  });
  /* CORNER JOIN (batch 11): two boards on walls that meet at a corner run INTO each other — each is
   * extended to the intersection of their centre lines, so the corner never shows a gap. */
  const runsMulti = runs.filter((r) => r.members.length >= 2);
  for (let i = 0; i < runsMulti.length; i++) {
    for (let j = i + 1; j < runsMulti.length; j++) {
      const A = runsMulti[i], B = runsMulti[j];
      if (A.wallId === B.wallId) continue;
      const dA = [A.cos, -A.sin], dB = [B.cos, -B.sin];
      const cross = dA[0] * dB[1] - dA[1] * dB[0];
      if (Math.abs(cross) < 0.2) continue;                                // parallel boards never meet
      const PA = [A.sin * A.q, A.cos * A.q], PB = [B.sin * B.q, B.cos * B.q];
      const rx = PB[0] - PA[0], rz = PB[1] - PA[1];
      const s = (rx * dB[1] - rz * dB[0]) / cross;
      const t = (rx * dA[1] - rz * dA[0]) / cross;
      const endOf = (r, v) => (v > r.s1 - 0.02 && v < r.s1 + 0.9) ? "s1" : (v < r.s0 + 0.02 && v > r.s0 - 0.9) ? "s0" : null;
      const ea = endOf(A, s), eb = endOf(B, t);
      if (!ea || !eb) continue;
      if (ea === "s1") A.s1 = Math.max(A.s1, s); else A.s0 = Math.min(A.s0, s);
      if (eb === "s1") B.s1 = Math.max(B.s1, t); else B.s0 = Math.min(B.s0, t);
    }
  }
  const out = [];
  let idx = 0;
  runs.forEach((r) => {
    if (r.members.length < 2) return;                                   // singles: byte-identical
    let segs = [[r.s0, r.s1]];
    golaPosts.forEach((po) => {
      if (po.wallId !== r.wallId) return;
      if (angDiff(po.angle || 0, r.ang) > 1e-6) return;
      if (!(po.botM < r.y + r.h / 2 - 0.002)) return;                 // ends above the plinth → the plinth runs under it
      let seat;
      try { seat = golaPostSeat(po, blob); } catch (e) { return; }
      if (!seat || !Number.isFinite(seat.sIn) || !(seat.dSec > 0)) return;
      const a = seat.sIn - seat.dSec / 2 - 0.001, b = seat.sIn + seat.dSec / 2 + 0.001;
      const next = [];
      segs.forEach(([x0, x1]) => {
        if (b <= x0 + 1e-9 || a >= x1 - 1e-9) { next.push([x0, x1]); return; }
        if (a > x0 + 0.01) next.push([x0, a]);
        if (b < x1 - 0.01) next.push([b, x1]);
      });
      segs = next;
    });
    const n = idx + 1;
    r.members.forEach((m) => { m.part.merged = true; m.spec.plinthRun = idx; });
    const parts = segs.map(([a, b], k) => {
      const sMid = (a + b) / 2;
      return {
        kind: "box", role: "kick", mat: r.mat || "leg",
        w: b - a, h: r.h, d: r.d,
        x: r.cos * sMid + r.sin * r.q, y: r.y, z: -r.sin * sMid + r.cos * r.q,
        rotY: r.ang, name: "Plinth run " + n + (k ? " seg " + (k + 1) : ""),
      };
    });
    specs.push({
      moduleId: "__plinthrun__" + idx,
      name: "Plinth run " + n,
      role: "plinth-run",
      angle: r.ang,
      visFrom: r.members[0].id,                                         // the run wears its first member's plinth finish
      runInfo: { members: r.members.map((m) => m.id), lengthM: Math.round((r.s1 - r.s0) * 1000) / 1000, segments: segs.length },
      parts,
    });
    out.push({ id: "__plinthrun__" + idx, wallId: r.wallId, members: r.members.map((m) => m.id), s0: r.s0, s1: r.s1, segments: segs.map(([a, b]) => [a, b]), angle: r.ang, q: r.q, qDoor: r.qDoor });
    idx++;
  });
  return out;
}

/* ── SEAM LINES (batch 10, 2026-09-29): the ~4 mm shadow reveal between two units that stand
 * flush on the same line of a wall — the exact rule UltraPresent's SeamLines renders, so the
 * orbit 3D shows the joints the render shows. One spec, one dark box part per joint at the
 * deeper unit's front face; a real gap (> 8 mm, e.g. the 30 mm gola clearance) gets none. ── */
export function applySeamLines(specs, state, lib) {
  const placements = (state && state.placements) || [];
  const byId = new Map(specs.map((s) => [s.moduleId, s]));
  const byLine = new Map();
  placements.forEach((p) => {
    if (!p || !p.wallId || p.island) return;
    const def = effDef(p, lib);
    if (!def) return;
    const role = def.role || "";
    const line = (role === "cabinet-wall" || role === "cabinet-stack" || role === "cabinet-bridge") ? "upper"
      : (role === "cabinet-base" || role === "cabinet-tall") ? "base" : null;
    if (!line) return;
    const spec = byId.get(p.id);
    if (!spec || !Array.isArray(spec.parts)) return;
    const body = spec.bodyEnvelope || spec.parts.find((pt) => pt && pt.kind === "box" && (pt.role === "carcass" || !pt.role));
    if (!body || !Number.isFinite(body.x) || !Number.isFinite(body.h)) return;
    const ang = spec.angle || 0;
    const cos = Math.cos(ang), sin = Math.sin(ang);
    const s = body.x * cos - body.z * sin;
    const q = body.x * sin + body.z * cos;
    const rec = {
      s0: s - def.width / 2, s1: s + def.width / 2,
      // the DOOR face: the carcass box is centred on the module and 25 mm shallower, so its own
      // front lies behind the doors and a reveal placed there was hidden (2026-09-29 user: "you
      // can't really see the separation lines in 3D — it must look like the render")
      qFront: q + (def.depth || (body.d || 0.57) + 0.025) / 2,
      y0: line === "base" ? Math.max(body.y - body.h / 2, 0.15) : body.y - body.h / 2,
      y1: body.y + body.h / 2,
      ang,
    };
    const key = p.wallId + "|" + line + "|" + (p.faceSign === -1 ? -1 : 1) + "|" + Math.round(ang * 1000);
    if (!byLine.has(key)) byLine.set(key, []);
    byLine.get(key).push(rec);
  });
  const parts = [];
  byLine.forEach((arr) => {
    arr.sort((a, b) => a.s0 - b.s0);
    for (let i = 0; i + 1 < arr.length; i++) {
      const a = arr[i], b = arr[i + 1];
      if (b.s0 - a.s1 > 0.008) continue;                       // a real gap — no seam
      const sB = (a.s1 + b.s0) / 2;
      const y0 = Math.max(a.y0, b.y0), y1 = Math.min(a.y1, b.y1);
      if (y1 - y0 < 0.1) continue;
      const qF = Math.max(a.qFront, b.qFront) - 0.002;
      const cos = Math.cos(a.ang), sin = Math.sin(a.ang);
      parts.push({
        kind: "box", role: "seamline", mat: "leg",
        w: 0.004, h: y1 - y0, d: 0.012,
        x: cos * sB + sin * qF, y: (y0 + y1) / 2, z: -sin * sB + cos * qF,
        rotY: a.ang, name: "seam line " + (parts.length + 1),
      });
    }
  });
  if (!parts.length) return [];
  specs.push({ moduleId: "__seamline__", name: "Seam lines", role: "seam-line", angle: 0, parts });
  return parts;
}

/* ── AUTO FILLER PANELS — GEOMETRY REMOVED (2026-09-15 user: "when I put close
 * two cabinets a wood shows up like it put it — must not happen!"). The old
 * fillerSpecs() turned the derived filler CUT-LIST (engine/fillerSchedule) into
 * front-stock strips wearing the neighbour's finish, so every close pair of
 * cabinets grew an uninvited "wood" panel in 3D, elevation and DXF. The
 * schedule itself (order payload, breakdown, Magic Fix) is untouched — nothing
 * RENDERS it any more. A filler the user places from the library is a real
 * placement and still renders like any module. ── */

/** buildBacksplashMesh (~33418) as a pure spec. DISABLED BY DEFAULT — the engine gates on
 *  window._splashEnabled !== true; pass { splashEnabled: true } to re-enable. Returns
 *  { w,h,d, x,y,z } for a thin tile strip on the back wall's interior face spanning
 *  worktop-top → upper line, only when a base-cabinet run exists. */
export function backsplashSpec(walls, placements, lib, wizard, opts) {
  if (!opts || opts.splashEnabled !== true) return null;
  // Only show the splashback when there's an actual base-cabinet run — never as a
  // floating white band across an otherwise empty wall.
  const _pl = placements || [];
  const _hasBase = _pl.some((p) => {
    const d = lib && lib[p && p.type];
    return d && /base/i.test((d.role || "") + " " + (d.category || ""));
  });
  if (!_hasBase) return null;
  const back = getBackWall(walls);
  if (!back) return null;
  const len = Math.hypot(back.end[0] - back.start[0], back.end[1] - back.start[1]);
  if (len <= 0) return null;
  // Splash spans from worktop top up to the wall-unit underside (upper line),
  // so it always agrees with the 3D mount + elevation dims via the same formula.
  const splashY1 = getWorktopTopM(wizard), splashY2 = getUpperLineM();
  const splashH = Math.max(0.01, splashY2 - splashY1);
  const cz = (back.start[1] + back.end[1]) / 2;
  const interiorZ = cz + WALL_THICKNESS / 2 + 0.005; // flush with interior face, room is at +z
  return {
    w: len, h: splashH, d: 0.012,
    x: (back.start[0] + back.end[0]) / 2,
    y: (splashY1 + splashY2) / 2,
    z: interiorZ,
  };
}
